/**
 * 删除购物车 Item
 */
if (!customElements.get("cart-remove")) {
  customElements.define(
    "cart-remove",
    class CartRemove extends HTMLElement {
      constructor() {
        super();

        this.addEventListener("click", (event) => {
          event.preventDefault();

          const cartItems = this.closest("cart-items");
          if (cartItems) cartItems.updateQuantity(this.dataset.index, 0);
        });
      }
    },
  );
}

if (!customElements.get("cart-items")) {
  customElements.define(
    "cart-items",
    class CartItems extends HTMLElement {
      constructor() {
        super();

        // 监听修改事件（输入框手输防抖，按钮点击即时更新）
        this.debouncedOnChange = webvista.debounce(
          this.updateQuantityByPayload.bind(this),
          500,
        );
        this.addEventListener("change", this.onChange.bind(this));

        this.sectionId = this.dataset.section;
        this.inDrawer = this.hasAttribute("data-in-drawer"); // 是否在抽屉中
        if (this.inDrawer) {
          // 获取焦点陷进抽屉
          this.drawerTrap = this.closest("[data-trap]");
        }

        this.lineItemStatusElement = document.getElementById(
          `Cart-Line-Item-Status-${this.sectionId}`,
        ); // 辅助设备状态管理
        this.cartItemsContainer = document.getElementById(
          `Cart-Items-${this.sectionId}`,
        );
        this.updateState = {
          isUpdatingByLine: new Map(),
          pendingByLine: new Map(),
          controllerByLine: new Map(),
          requestSeqByLine: new Map(),
          desiredQtyByLine: new Map(),
          debounceTimerByLine: new Map(),
        };

        // 监听页面卸载，立即发送所有 debounce 中的更新
        window.addEventListener('beforeunload', () => {
          this.flushAllDebounce();
        });

        // 页面加载时恢复 localStorage 中的待提交数量
        this.restorePendingQuantities();

        // 判断礼品包装数量和item数量是否一致
        if (this.hasAttribute("data-gift-wrap-id")) {
          // 数量不一致，更新礼品包装数量
          if (
            parseInt(this.dataset.giftWrapQuantity) !==
            parseInt(this.dataset.itemQuantity)
          ) {
            this.updateQuantity(
              this.dataset.giftWrapIndex,
              this.dataset.itemQuantity,
              null,
              this.dataset.giftWrapId,
            );
          }
        }
      }

      /**
       * 处理变化事件
       * @param event
       */
      onChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;

        const payload = {
          line: input.dataset.index,
          quantity: input.value,
          focusableElement: document.activeElement,
          variantId: input.dataset.quantityVariantId,
        };

        if (input.dataset.instantUpdate === "true") {
          this.updateQuantityByPayload(payload);
        } else {
          this.debouncedOnChange(payload);
        }
      }

      updateQuantityByPayload(payload) {
        if (!payload) return;
        this.updateQuantity(
          payload.line,
          payload.quantity,
          payload.focusableElement,
          payload.variantId,
        );
      }

      isStaleLineResponse(lineKey, requestId) {
        return this.updateState.requestSeqByLine.get(lineKey) !== requestId;
      }

      syncLineItemQuantityInputs(line, quantity) {
        if (!line || typeof quantity === "undefined") return;
        const quantityInputs = this.querySelectorAll(
          `.quantity-input[data-index='${line}']`,
        );
        quantityInputs.forEach((input) => {
          input.value = quantity;
          input.setAttribute("value", quantity);
        });
      }

      applyFinalLineQuantity(lineKey) {
        const quantity = this.updateState.desiredQtyByLine.get(lineKey);
        if (typeof quantity === "undefined") return;
        const line = Number(lineKey);
        requestAnimationFrame(() => {
          this.syncLineItemQuantityInputs(line, quantity);
        });
      }

      flushAllDebounce() {
        const state = this.updateState;
        // 遍历所有 debounce 定时器，清空并立即执行
        state.debounceTimerByLine.forEach((timer, lineKey) => {
          clearTimeout(timer);
          // 从输入框获取当前数量
          const line = Number(lineKey);
          const input = this.querySelector(`input.quantity-input[data-index="${line}"]`);
          if (input) {
            const quantity = input.value;
            const variantId = input.dataset.quantityVariantId;
            // 保存到 localStorage 以防请求被取消
            this.savePendingQuantity(line, quantity, variantId);
            this.executeQuantityUpdate(line, quantity, null, variantId);
          }
        });
        state.debounceTimerByLine.clear();
      }

      savePendingQuantity(line, quantity, variantId = null) {
        try {
          const key = `cart_pending_qty_${this.sectionId}`;
          const pendingRaw = JSON.parse(localStorage.getItem(key) || "{}");
          const pending =
            pendingRaw && typeof pendingRaw === "object" && !Array.isArray(pendingRaw)
              ? pendingRaw
              : {};
          const lineKey = String(line);
          const quantityNumber = Number(quantity);
          const safeVariantId =
            String(variantId || "").trim() ||
            String(
              this.querySelector(`input.quantity-input[data-index="${lineKey}"]`)
                ?.dataset.quantityVariantId || "",
            ).trim();

          if (!lineKey || Number.isNaN(quantityNumber) || !safeVariantId) return;

          pending[lineKey] = {
            quantity: String(quantityNumber),
            variantId: safeVariantId,
            timestamp: Date.now(),
          };
          localStorage.setItem(key, JSON.stringify(pending));
        } catch (e) {
          console.warn('[Cart] Failed to save pending quantity:', e);
        }
      }

      clearPendingQuantity(line) {
        try {
          const key = `cart_pending_qty_${this.sectionId}`;
          const pendingRaw = JSON.parse(localStorage.getItem(key) || '{}');
          if (
            !pendingRaw ||
            typeof pendingRaw !== "object" ||
            Array.isArray(pendingRaw)
          ) {
            localStorage.removeItem(key);
            return;
          }

          delete pendingRaw[String(line)];
          if (Object.keys(pendingRaw).length === 0) {
            localStorage.removeItem(key);
          } else {
            localStorage.setItem(key, JSON.stringify(pendingRaw));
          }
        } catch (e) {
          console.warn('[Cart] Failed to clear pending quantity:', e);
        }
      }

      restorePendingQuantities() {
        try {
          const key = `cart_pending_qty_${this.sectionId}`;
          const pendingRaw = JSON.parse(localStorage.getItem(key) || '{}');
          if (
            !pendingRaw ||
            typeof pendingRaw !== "object" ||
            Array.isArray(pendingRaw)
          ) {
            localStorage.removeItem(key);
            return;
          }

          const pending = { ...pendingRaw };
          const now = Date.now();
          const pendingMaxAge = 10 * 60 * 1000;
          let hasPrunedEntry = false;

          Object.entries(pendingRaw).forEach(([line, record]) => {
            const input = this.querySelector(`input.quantity-input[data-index="${line}"]`);
            if (!input || !record || typeof record !== "object" || Array.isArray(record)) {
              delete pending[line];
              hasPrunedEntry = true;
              return;
            }

            const pendingQuantity = Number(record.quantity);
            const pendingVariantId = String(record.variantId || "").trim();
            const pendingTimestamp = Number(record.timestamp);
            const currentVariantId = String(input.dataset.quantityVariantId || "").trim();
            const isFresh =
              Number.isFinite(pendingTimestamp) &&
              now - pendingTimestamp >= 0 &&
              now - pendingTimestamp <= pendingMaxAge;

            if (
              Number.isNaN(pendingQuantity) ||
              !pendingVariantId ||
              !currentVariantId ||
              currentVariantId !== pendingVariantId ||
              !isFresh
            ) {
              delete pending[line];
              hasPrunedEntry = true;
              return;
            }

            const quantity = String(pendingQuantity);
            this.syncLineItemQuantityInputs(line, quantity);
            this.executeQuantityUpdate(line, quantity, null, pendingVariantId);
          });

          if (hasPrunedEntry) {
            if (Object.keys(pending).length === 0) {
              localStorage.removeItem(key);
            } else {
              localStorage.setItem(key, JSON.stringify(pending));
            }
          }
        } catch (e) {
          console.warn('[Cart] Failed to restore pending quantities:', e);
        }
      }

      getCartItemElement(line) {
        return this.querySelector(`#Cart-Item-${this.sectionId}-${line}`);
      }

      setLineSyncing(line, syncing) {
        const cartItemElement = this.getCartItemElement(line);
        if (!cartItemElement) return;
        cartItemElement.classList.toggle("cart-item--syncing", syncing);
      }

      /**
       * 更新购物车数量
       * @param line cart-item Id
       * @param quantity 数量
       * @param focusableElement 可聚焦元素对象
       * @param variantId 变体id
       */
      updateQuantity(line, quantity, focusableElement, variantId) {
        const lineKey = String(line);
        const state = this.updateState;
        const quantityNumber = Number(quantity);
        if (!lineKey || Number.isNaN(quantityNumber)) return;
        if (typeof CartCheckoutWaitGuard !== "undefined") {
          CartCheckoutWaitGuard.hold();
        }

        // 立即更新输入框显示（乐观更新）
        this.syncLineItemQuantityInputs(line, quantity);

        // 保存到 localStorage，防止刷新页面丢失
        this.savePendingQuantity(line, quantity, variantId);

        // 添加视觉反馈：等待提交状态
        const cartItem = this.getCartItemElement(line);
        if (cartItem) cartItem.classList.add('cart-item--pending-update');

        // 清除之前的防抖定时器
        if (state.debounceTimerByLine.has(lineKey)) {
          clearTimeout(state.debounceTimerByLine.get(lineKey));
        }

        // 设置新的防抖定时器：用户停止点击 1.5 秒后才发送请求
        const timer = setTimeout(() => {
          this.executeQuantityUpdate(line, quantity, focusableElement, variantId);
        }, 1500);

        state.debounceTimerByLine.set(lineKey, timer);
      }

      executeQuantityUpdate(line, quantity, focusableElement, variantId) {
        const lineKey = String(line);
        const state = this.updateState;
        const quantityNumber = Number(quantity);
        if (!lineKey || Number.isNaN(quantityNumber)) return;

        // 清除防抖定时器和视觉反馈
        state.debounceTimerByLine.delete(lineKey);
        const cartItem = this.getCartItemElement(line);
        if (cartItem) cartItem.classList.remove('cart-item--pending-update');

        // 同一 line 请求进行中时，仅保留最后一次意图
        if (state.isUpdatingByLine.get(lineKey)) {
          state.pendingByLine.set(lineKey, {
            line,
            quantity,
            focusableElement,
            variantId,
          });
          return;
        }

        state.isUpdatingByLine.set(lineKey, true);
        this.enableLoading(line);
        this.setLineSyncing(line, true);

        // 仅取消同一 line 的上一请求
        if (state.controllerByLine.has(lineKey)) {
          state.controllerByLine.get(lineKey).abort();
        }
        const controller = new AbortController();
        state.controllerByLine.set(lineKey, controller);
        const requestId = (state.requestSeqByLine.get(lineKey) || 0) + 1;
        state.requestSeqByLine.set(lineKey, requestId);

        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map(
            (section) => section.section,
          ),
          sections_url: window.location.pathname,
        });

        fetch(window["routes"]["cart_change_url"], {
          ...webvista.fetchConfig(),
          ...{ body },
          signal: controller.signal,
        })
          .then((response) => {
            return response.json();
          })
          .then((responseJson) => {
            if (this.isStaleLineResponse(lineKey, requestId)) return;

            if (responseJson.errors) {
              this.restoreLineItemQuantityInputs(line);
              return this.showError(responseJson.errors);
            }

            const updatedValue = responseJson.items[line - 1]
              ? responseJson.items[line - 1].quantity
              : undefined;
            let message = "";
            const items = this.querySelectorAll(".cart-item");

            const hasRenderedLineItem = Boolean(this.getCartItemElement(line));
            if (
              hasRenderedLineItem &&
              items.length === responseJson.items.length &&
              updatedValue !== quantityNumber
            ) {
              if (typeof updatedValue === "undefined") {
                message = window["cartStrings"]["error"];
              } else {
                message = window["cartStrings"]["quantityError"].replace(
                  "[quantity]",
                  updatedValue,
                );
              }

              this.restoreLineItemQuantityInputs(line);
              return this.showError(message);
            }

            // 若该行已有更新中的下一次操作，则跳过当前中间态渲染，避免错误数值闪现
            if (state.pendingByLine.has(lineKey)) return;
            state.desiredQtyByLine.set(lineKey, String(updatedValue));

            // 判断是否有包装，如果有的话更新包装数量
            const giftWrapLine = this.dataset.giftWrapIndex;
            const giftWrapping = document.getElementById(
              `Cart-Gift-Wrapping-${this.sectionId}`,
            );

            if (giftWrapping && giftWrapLine != null && giftWrapLine !== line) {
              // 更新包装
              const item_quantity =
                responseJson["item_count"] -
                parseInt(this.dataset.giftWrapQuantity);
              if (item_quantity > 0) {
                this.updateQuantity(
                  giftWrapLine,
                  item_quantity,
                  focusableElement,
                  variantId,
                );
              } else {
                giftWrapping.removeWrapping();
              }
            } else {
              // 处理购物车更新
              this.handleCartUpdate(responseJson, variantId, focusableElement);
              this.applyFinalLineQuantity(lineKey);
              // 成功后清除 localStorage
              this.clearPendingQuantity(line);
            }
          })
          .catch((error) => {
            // 忽略被取消的请求（用户快速连续操作导致的正常取消）
            if (error.name === "AbortError") return;
            if (this.isStaleLineResponse(lineKey, requestId)) return;

            // 区分第三方插件的 JS 运行时错误与真正的购物车/网络错误
            // VO Product Options 等插件在 DOM 更新后可能抛出 ReferenceError/TypeError
            // 这些不是购物车操作失败，不应向用户显示错误提示
            if (error instanceof ReferenceError || error instanceof SyntaxError) {
              console.warn("[Cart] Third-party script error (ignored):", error.message);
              return;
            }

            // 真正的购物车/网络错误，显示提示
            this.showError(window["cartStrings"]["error"]);
          })
          .finally(() => {
            if (state.controllerByLine.get(lineKey) === controller) {
              state.controllerByLine.delete(lineKey);
            }
            if (this.isStaleLineResponse(lineKey, requestId)) return;

            // 当前 line 若有暂存操作，继续发送最后一次
            const pending = state.pendingByLine.get(lineKey);
            if (pending) {
              state.pendingByLine.delete(lineKey);
              state.isUpdatingByLine.set(lineKey, false);
              this.executeQuantityUpdate(
                pending.line,
                pending.quantity,
                pending.focusableElement,
                pending.variantId,
              );
              return;
            }

            state.isUpdatingByLine.set(lineKey, false);
            this.disableLoading(line);
            this.setLineSyncing(line, false);
            state.desiredQtyByLine.delete(lineKey);
          });
      }

      /**
       * 处理变化后的更新
       * 更新网站显示，处理焦点，发送广播
       * @param responseJson
       * @param variantId
       * @param focusableElement 可聚焦的焦点元素
       */
      handleCartUpdate(responseJson, variantId, focusableElement) {
        // 更新内容
        SectionDynamicUpdate.updateSections(
          this.getSectionsToRender(),
          responseJson.sections,
        );

        // 同步修正行级折扣标签（含 VO 选项价）
        VODiscountFix.updateLineDiscountTags(responseJson);

        // 重新进入焦点陷阱
        if (this.drawerTrap) {
          webvista.trapFocus(this.drawerTrap, focusableElement);
        } else if (focusableElement) {
          focusableElement.focus();
        }

        // 发送事件广播
        webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          productVariantId: variantId,
          cartData: responseJson,
        });
      }

      /**
       * 还原 Line Item 中的所有输入框的值
       * 还原为属性中的原始值
       */
      restoreLineItemQuantityInputs(line) {
        if (!line) return;

        const quantityInputs = this.querySelectorAll(
          `.quantity-input[data-index='${line}']`,
        );
        quantityInputs.forEach((input) => {
          input.value = input.getAttribute("value");
        });
      }

      /**
       * 返回需要更新的Sections
       * @returns {[{section: string, selector: string, id: string}]}
       */
      getSectionsToRender() {
        const sections = [
          {
            id: "Cart-Icon-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
        ];

        if (this.inDrawer) {
          sections.push({
            id: "Cart-Drawer",
            section: this.sectionId,
            selector: "#Cart-Drawer-Details",
          });
        } else {
          sections.push({
            id: "Main-Cart",
            section: this.sectionId,
            selector: "#Main-Cart-Details",
          });
        }

        return sections;
      }

      /**
       * 显示错误提示
       * @param message
       */
      showError(message = null) {
        if (!message) return;

        webvista.popToast(message, "error");
      }

      /**
       * Loading 状态
       * @param line line item
       */
      enableLoading(line = null) {
        this.cartItemsContainer.classList.add("cart-items--disabled");

        if (line) {
          const cartItemElement = this.querySelector(
            `#Cart-Item-${this.sectionId}-${line}`,
          );
          if (!cartItemElement) return;

          cartItemElement.classList.add("loading");
        }

        document.activeElement.blur();
        this.lineItemStatusElement.setAttribute("aria-hidden", "false");
      }

      /**
       * End Loading 状态
       * @param line
       */
      disableLoading(line = null) {
        this.cartItemsContainer.classList.remove("cart-items--disabled");

        if (line) {
          const cartItemElement = this.querySelector(
            `#Cart-Item-${this.sectionId}-${line}`,
          );
          if (!cartItemElement) return;

          cartItemElement.classList.remove("loading");
        }

        this.lineItemStatusElement.setAttribute("aria-hidden", "true");
      }
    },
  );
}

if (!customElements.get("free-shipping-progress")) {
  customElements.define(
    "free-shipping-progress",
    class FreeShippingProgress extends HTMLElement {
      /**
       * data-total-amount 购物车总价金额，单位分
       * data-free-threshold 包邮门槛金额，单位元
       * data-confetti 触发彩带庆祝效果
       */
      constructor() {
        super();

        this.renderProgress();
      }

      get progressBar() {
        return this.querySelector(".free-shipping-progress");
      }

      get messageWrapper() {
        return this.querySelector(".free-shipping-message");
      }

      /**
       * 渲染包邮进度条
       */
      renderProgress() {
        const cartTotalAmount = this.dataset.totalAmount;
        const freeThreshold = this.dataset.freeThreshold;

        if (
          !webvista.isNumeric(cartTotalAmount) ||
          !webvista.isNumeric(freeThreshold)
        )
          return;

        const freeThresholdLocal =
          Math.round(parseInt(freeThreshold) * (Shopify.currency.rate || 1)) *
          100; // 门槛金额汇率转换，单位分

        let progress;
        if (cartTotalAmount - freeThresholdLocal >= 0) {
          progress = 100;
          this.messageWrapper.innerHTML =
            window["cartStrings"]["freeShippingUnlockedMessage"];
          this.classList.add("free-has-unlocked");
        } else {
          progress = (cartTotalAmount * 100) / freeThresholdLocal;

          const amountToQualify = freeThresholdLocal - cartTotalAmount;
          this.messageWrapper.innerHTML = window["cartStrings"][
            "freeShippingLockedMessage"
          ].replace(
            "[amount]",
            webvista.formatPriceAmount(
              amountToQualify,
              Shopify.currency.active,
            ),
          );
        }

        // 进度条进度
        if (this.progressBar)
          this.progressBar.style.setProperty(
            "--free-shipping-progress",
            `${progress}%`,
          );

        // 触发庆祝彩带特效
        if (this.hasAttribute("data-confetti")) {
          document.dispatchEvent(
            new CustomEvent("freeShippingUnlocked", {
              detail: { status: progress >= 100 },
            }),
          );
        }
      }
    },
  );
}

/**
 * 用于购物车备注弹窗的自定义元素
 */
if (!customElements.get("cart-note-modal")) {
  customElements.define(
    "cart-note-modal",
    class CartNoteModal extends ModalDialog {
      constructor() {
        super();

        const form = this.querySelector("form");
        if (!form) return;

        this.submitButton = this.querySelector("button[type=submit]");
        form.addEventListener("submit", this.formSubmit.bind(this));
      }

      /**
       * 处理保存备注
       * @param event
       */
      formSubmit(event) {
        event.preventDefault();
        this.startLoading();

        const formData = new FormData(event.target);
        const formDataJson = Object.fromEntries(formData.entries());
        const body = JSON.stringify(formDataJson);

        fetch(window["routes"]["cart_update_url"], {
          ...webvista.fetchConfig(),
          ...{ body },
        })
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.error) {
              throw new Error("Cart update error");
            }

            webvista.popToast(
              window["cartStrings"]["addToNoteSuccess"],
              "success",
            );
            this.hide();
          })
          .catch((error) => {
            webvista.popToast(window["cartStrings"]["addToNoteError"], "error");
          })
          .finally(() => {
            this.endLoading();
          });
      }

      startLoading() {
        this.submitButton.classList.add("loading");
        this.submitButton.setAttribute("disabled", "disabled");
      }

      endLoading() {
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("disabled");
      }
    },
  );
}

/**
 * 订单运费预估
 */
if (!customElements.get("shipping-calculator-modal")) {
  customElements.define(
    "shipping-calculator-modal",
    class ShippingCalculatorModal extends ModalDialog {
      static MAX_ATTEMPTS = 3;
      constructor() {
        super();

        this.asyncAttemptCount = 0; // Async请求尝试次数

        const form = this.querySelector("form");
        if (!form) return;

        form.addEventListener("submit", this.prepareRate.bind(this));

        this.submitButton = this.querySelector("button[type=submit]");
        this.successMessageElement = this.querySelector(".alert-success");
      }

      /**
       * 请求查询物流信息
       * @param event
       */
      prepareRate(event) {
        event.preventDefault();

        this.hideResults();
        this.startLoading();

        const formData = new FormData(event.target);
        const shipping_address = {
          country: formData.get("country"),
          province: formData.get("province"),
          zip: formData.get("zip"),
        };
        const body = JSON.stringify({ shipping_address: shipping_address });

        fetch(`${window["routes"]["cart_url"]}/prepare_shipping_rates.json`, {
          ...webvista.fetchConfig(),
          ...{ body },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Network response was not ok: ${response.statusText}`,
              );
            }
            return response.json();
          })
          .then((response) => {
            if (response == null || response.ok) {
              this.asyncRate({ shipping_address: shipping_address });
            } else {
              throw new Error("Prepare shipping rates error");
            }
          })
          .catch((error) => {
            this.showError();
          });
      }

      /**
       * 异步查询返回的信息
       * @param params
       */
      asyncRate(params) {
        fetch(
          `${window["routes"]["cart_url"]}/async_shipping_rates.json?` +
          this.serialize(params),
          { ...webvista.fetchConfig("json", "GET") },
        )
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (
              response != null &&
              response["shipping_rates"] != null &&
              response["shipping_rates"].length > 0
            ) {
              this.showResults(response);
            } else {
              if (
                ++this.asyncAttemptCount >= ShippingCalculatorModal.MAX_ATTEMPTS
              ) {
                this.showError();
              } else {
                setTimeout(() => this.asyncRate(params), 3000);
              }
            }
          })
          .catch((error) => {
            this.showError();
          });
      }

      startLoading() {
        this.submitButton.classList.add("loading");
        this.submitButton.setAttribute("disabled", "disabled");
      }

      endLoading() {
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("disabled");
      }

      showError() {
        this.endLoading();
        webvista.popToast(
          window["cartStrings"]["estimateShippingError"],
          "error",
        );
      }

      /**
       * 显示查询到的结果
       * @param result
       */
      showResults(result) {
        this.endLoading();

        const rates = result["shipping_rates"];
        if (!rates || rates.length <= 0 || !this.successMessageElement) return;

        // 标题修改
        const countElement =
          this.successMessageElement.querySelector(".result-count");
        countElement.innerText = rates.length;

        // 显示查询到的物流列表
        const template =
          this.successMessageElement.querySelector("template").content; // 消息模板
        const fragment = document.createDocumentFragment();
        rates.forEach((rate) => {
          const clone = document.importNode(template, true);

          clone.querySelector(".presentment-name").textContent =
            rate["presentment-name"] || rate["name"];
          clone.querySelector(".delivery-price").textContent = window[
            "priceFormatTemplate"
          ].replace(/0([,.]0{0,2})?/, rate["price"]);

          let deliveryDate = rate["delivery_date"];
          if (rate["delivery_range"] && Array.isArray(rate["delivery_range"])) {
            if (rate["delivery_range"].length > 1) {
              if (rate["delivery_range"][0] === rate["delivery_range"][1]) {
                deliveryDate = rate["delivery_range"][0];
              } else {
                deliveryDate = rate["delivery_range"].join(" - ");
              }
            } else {
              deliveryDate = rate["delivery_range"][0];
            }
          }

          if (!deliveryDate || deliveryDate.trim() === "") {
            clone.querySelector(".delivery-date-wrapper")?.remove();
          } else {
            clone.querySelector(".delivery-date").textContent = deliveryDate;
          }

          fragment.appendChild(clone);
        });

        this.successMessageElement
          .querySelector(".message-list")
          ?.appendChild(fragment);
        this.successMessageElement.removeAttribute("hidden");
        this.endLoading();
      }

      hideResults() {
        if (this.successMessageElement) {
          this.successMessageElement.querySelector(".message-list").innerHTML =
            "";
          this.successMessageElement.setAttribute("hidden", "true");
        }
      }

      serialize(obj, prefix) {
        const str = Object.keys(obj)
          .filter((key) => obj.hasOwnProperty(key))
          .map((key) => {
            const k = prefix ? `${prefix}[${key}]` : key;
            const v = obj[key];
            return v !== null && typeof v === "object"
              ? this.serialize(v, k)
              : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          });
        return str.join("&");
      }
    },
  );
}

/**
 * 礼品包装
 */
if (!customElements.get("gift-wrapping")) {
  customElements.define(
    "gift-wrapping",
    class GiftWrapping extends HTMLElement {
      constructor() {
        super();

        this.giftWrapId = this.dataset.giftWrappingId;
        if (!this.giftWrapId) return;
        this.itemQuantity = Math.max(0, parseInt(this.dataset.itemQuantity)); // 订单中的item总数量

        this.cartItems = document.getElementById(
          `Cart-Items-Main-${this.dataset.section}`,
        );

        const boundChange = webvista.debounce(this.onChange.bind(this), 1000);
        this.checkInput = this.querySelector('input[type="checkbox"]');
        this.checkInput.addEventListener("change", boundChange);
      }

      onChange(event) {
        if (event.target.checked) {
          this.setWrapping();
        } else {
          this.removeWrapping();
        }
      }

      /**
       * 添加包裹
       */
      setWrapping() {
        const body = JSON.stringify({
          updates: {
            [this.giftWrapId]: this.itemQuantity,
          },
          attributes: {
            "gift-wrap": "Yes",
          },
          sections: this.cartItems
            .getSectionsToRender()
            .map((section) => section.section),
          sections_url: window.location.pathname,
        });

        this.fetchData(body);
      }

      /**
       * 移除包裹
       */
      removeWrapping() {
        const body = JSON.stringify({
          updates: {
            [this.giftWrapId]: 0,
          },
          attributes: {
            "gift-wrap": "",
          },
          sections: this.cartItems
            .getSectionsToRender()
            .map((section) => section.section),
          sections_url: window.location.pathname,
        });

        this.fetchData(body);
      }

      fetchData(body) {
        this.enableLoading();
        if (typeof CartCheckoutWaitGuard !== "undefined") {
          CartCheckoutWaitGuard.hold();
        }
        fetch(window["routes"]["cart_update_url"], {
          ...webvista.fetchConfig(),
          body,
        })
          .then((response) => {
            return response.json();
          })
          .then((responseJson) => {
            this.cartItems.handleCartUpdate(
              responseJson,
              this.giftWrapId,
              this.checkInput,
            );
          })
          .catch((error) => {
            webvista.popToast(
              window["accessibilityStrings"]["unknownError"],
              "error",
            );
          })
          .finally(() => {
            this.disableLoading();
          });
      }

      enableLoading() {
        this.cartItems.enableLoading();

        this.checkInput.setAttribute("disabled", "true");
      }

      disableLoading() {
        this.cartItems.disableLoading();

        this.checkInput.removeAttribute("disabled");
      }
    },
  );
}

/**
 * 应用折扣码
 */
if (!customElements.get("cart-code-modal")) {
  customElements.define(
    "cart-code-modal",
    class CartCodeModal extends ModalDialog {
      enqueueToast(message, type) {
        if (!message) return;
        setTimeout(() => {
          webvista.popToast(message, type);
        }, 100);
      }

      constructor() {
        super();

        const form = this.querySelector("form");
        if (!form) return;

        this.sectionId = this.dataset.section;
        this.codes = this.dataset.codes ? this.dataset.codes.split(",") : []; // 已经应用的折扣码

        this.submitButton = this.querySelector("button[type=submit]");
        form.addEventListener("submit", this.formSubmit.bind(this));

        this.codeList = this.querySelector(".applied-code-list");
        if (this.codeList)
          this.codeList.addEventListener("click", this.codeRemove.bind(this));
      }

      /**
       * 处理提交折扣码
       * @param {Event} event
       */
      formSubmit(event) {
        event.preventDefault();

        const formData = new FormData(event.target);
        const raw = formData.get("discount");
        const code = (raw ? String(raw) : "").trim();

        if (!code) return;

        const hasCode = this.codes
          .map((c) => (c || "").trim().toLowerCase())
          .includes(code.toLowerCase());
        if (hasCode) {
          this.enqueueToast(
            window["cartStrings"]["codeAlreadyApplied"],
            "warning",
          );
          return;
        }

        const updatedCodes = [...this.codes, code].join(",");
        this.updateDiscount(updatedCodes).then((response) => {
          this.enqueueToast(
            window["cartStrings"]["applyCodeSuccess"],
            "success",
          );
          this.hide();
        });
      }

      /**
       * 移除折扣应用
       */
      codeRemove(event) {
        const button = event.target.closest(".remove-code-button");
        if (!button || !this.contains(button)) return;

        const code = (button.dataset.code || "").trim();

        // 过滤掉要删除的 code
        const updatedCodes = this.codes.filter(
          (c) => (c || "").trim() !== code,
        );

        // 请求更新
        this.updateDiscount(updatedCodes.join(",")).then((response) => {
          // 移除按钮对应的 DOM 节点
          button.closest(".applied-code")?.remove();

          this.enqueueToast(
            window["cartStrings"]["removeCodeSuccess"],
            "success",
          );
        });
      }

      /**
       * 传入折扣码，更新购物车并刷新需要的 sections
       * 支持多个折扣码比如 'code1,code2,code3'
       * @param {string} discount
       */
      async updateDiscount(discount) {
        this.startLoading();
        if (typeof CartCheckoutWaitGuard !== "undefined") {
          CartCheckoutWaitGuard.hold();
        }
        const sections = [
          ...new Set(
            this.getSectionsToRender().map((section) => section.section),
          ),
        ];

        const body = JSON.stringify({
          discount,
          sections,
          sections_url: window.location.pathname,
        });

        try {
          const res = await fetch(window["routes"]["cart_update_url"], {
            ...webvista.fetchConfig(),
            ...{ body },
          });

          if (!res.ok) {
            throw new Error(`Network error: ${res.status}`);
          }

          const response = await res.json();

          if (response.error) {
            throw new Error(response.error);
          }

          // 检查无效折扣码 —— 直接抛异常
          if (Array.isArray(response.discount_codes)) {
            const invalidCodes = response.discount_codes
              .filter((d) => d && d.applicable === false)
              .map((d) => d.code);

            if (invalidCodes.length > 0) {
              const codesText = invalidCodes.join(", ");
              throw new Error(
                window["cartStrings"]["codeInvalid"].replace(
                  "[code]",
                  codesText,
                ),
              );
            }
          }

          // 正常更新 sections
          if (response.sections) {
            SectionDynamicUpdate.updateSections(
              this.getSectionsToRender(),
              response.sections,
            );
          }

          // 折扣码变更后：把最新码传给标签渲染器
          const freshCodes = (response.discount_codes || [])
            .filter(d => d.applicable !== false)
            .map(d => d.code);

          if (typeof CartCheckoutWaitGuard !== "undefined") {
            CartCheckoutWaitGuard.requestSync();
          }

          if (typeof DrStackedDiscountTags !== 'undefined') {
            DrStackedDiscountTags.refreshAfterCodeChange(freshCodes);
          }

          return response;
        } catch (err) {
          // 统一异常处理（包含无效折扣码和请求错误）
          this.enqueueToast(err.message, "error");
          // console.error(err); // 按需调试
          throw err;
        } finally {
          this.endLoading();
        }
      }

      /**
       * 获取需要重新渲染的购物车 Section 配置
       *
       * 根据元素是否具有 `data-in-drawer` 属性，
       * 返回对应的 Section 信息数组。
       *
       * @returns {{id: string, section: string, selector: string}[]}
       */
      getSectionsToRender() {
        if (this.hasAttribute("data-in-drawer")) {
          return [
            {
              id: "Cart-Drawer",
              section: this.sectionId,
              selector: "#Cart-Drawer-Details",
            },
          ];
        } else {
          return [
            {
              id: "Main-Cart",
              section: this.sectionId,
              selector: "#Main-Cart-Details",
            },
          ];
        }
      }

      startLoading() {
        if (this.submitButton) {
          this.submitButton.classList.add("loading");
          this.submitButton.setAttribute("disabled", "disabled");
        }
      }

      endLoading() {
        if (this.submitButton) {
          this.submitButton.classList.remove("loading");
          this.submitButton.removeAttribute("disabled");
        }
      }
    },
  );
}




/**
 * VO Product Options 折扣修复
 * 从购物车商品的 properties 中提取定制选项价格，计算正确的折扣
 */
const VODiscountFix = {
  syncInFlight: null,
  lastSyncAt: 0,
  syncTimer: null,
  pendingCartData: null,
  lastAppliedSignature: "",
  lineTotalsLoadingActive: false,
  lineTotalsLoadingReleaseTimer: null,
  lineVOUnitPriceCache: new Map(),
  lineBaseUnitPriceCache: new Map(),
  lineVisualState: new Map(),
  lineVisualPendingRevealKeys: new Set(),
  lineVisualGlobalFailSafeTimer: null,
  lineVisualQuietTimer: null,
  lineVisualMutationObserver: null,
  isVisualQuietReady: false,
  LINE_VISUAL_STABLE_MS: 650,
  LINE_VISUAL_QUIET_MS: 1100,
  LINE_VISUAL_FAILSAFE_MS: 7000,

  getLineTotalWrappers() {
    return Array.from(document.querySelectorAll(".cart-item-total-price.has-loading"));
  },

  syncLineTotalsLoadingState() {
    this.getLineTotalWrappers().forEach((wrapper) => {
      wrapper.classList.toggle("loading", this.lineTotalsLoadingActive);
    });
  },

  setLineTotalsLoading(isLoading) {
    if (this.lineTotalsLoadingReleaseTimer) {
      clearTimeout(this.lineTotalsLoadingReleaseTimer);
      this.lineTotalsLoadingReleaseTimer = null;
    }

    this.lineTotalsLoadingActive = Boolean(isLoading);
    this.syncLineTotalsLoadingState();
  },

  releaseLineTotalsLoading(delay = 120) {
    if (!this.lineTotalsLoadingActive) return;
    if (this.lineTotalsLoadingReleaseTimer) {
      clearTimeout(this.lineTotalsLoadingReleaseTimer);
    }

    this.lineTotalsLoadingReleaseTimer = setTimeout(() => {
      this.lineTotalsLoadingActive = false;
      this.syncLineTotalsLoadingState();
      this.lineTotalsLoadingReleaseTimer = null;
    }, delay);
  },

  extractPriceFromProperty(value) {
    if (!value || typeof value !== 'string') return 0;

    const priceMatch = value.match(/\$\s*([\d,]+(?:\.\d{1,2})?)/);
    if (priceMatch) {
      const priceStr = priceMatch[1].replace(/,/g, '');
      const price = parseFloat(priceStr);
      if (!isNaN(price)) {
        return Math.round(price * 100);
      }
    }
    return 0;
  },

  normalizeQuantity(value) {
    return Math.max(1, parseInt(value, 10) || 1);
  },

  /**
   * 计算购物车中所有 VO 选项的总价格（美分）
   * 注意：需要将每个 item 的 VO 选项价格乘以商品数量
   * @param {Array} items - 购物车商品数组
   * @returns {number} 总价格（美分）
   */
  calculateVOOptionsTotal(items) {
    let total = 0;

    if (!items || !Array.isArray(items)) return total;

    items.forEach((item) => {
      const linePriceState = this.getLinePriceState(item);
      total += linePriceState.missingVOPriceCents;
    });

    return total;
  },

  calculateItemVOPrice(item) {
    let itemVoPrice = 0;
    if (item && item.properties && typeof item.properties === "object") {
      Object.values(item.properties).forEach((value) => {
        itemVoPrice += this.extractPriceFromProperty(value);
      });
    }
    return itemVoPrice;
  },

  parseCents(value) {
    if (value == null) return null;
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.round(value);
    }

    const normalized = String(value).replace(/[^\d.-]/g, "");
    if (!normalized) return null;
    const number = Number(normalized);
    return Number.isFinite(number) ? Math.round(number) : null;
  },

  getLineFinalElements(itemKey) {
    if (!itemKey) return [];
    return Array.from(
      document.querySelectorAll(`.cart-item-line-final[data-key="${itemKey}"]`),
    );
  },

  getLineOriginalElements(itemKey) {
    if (!itemKey) return [];
    return Array.from(
      document.querySelectorAll(`.cart-item-line-original[data-key="${itemKey}"]`),
    );
  },

  getManagedLineFinalElement(itemKey) {
    if (!itemKey) return null;
    const mainCartLineFinal = document.querySelector(
      `#Main-Cart .cart-item-line-final[data-key="${itemKey}"]`,
    );
    if (mainCartLineFinal) return mainCartLineFinal;

    const cartDrawerLineFinal = document.querySelector(
      `#Cart-Drawer .cart-item-line-final[data-key="${itemKey}"]`,
    );
    if (cartDrawerLineFinal) return cartDrawerLineFinal;

    return this.getLineFinalElements(itemKey)[0] || null;
  },

  getManagedLineOriginalElement(itemKey) {
    if (!itemKey) return null;
    const mainCartLineOriginal = document.querySelector(
      `#Main-Cart .cart-item-line-original[data-key="${itemKey}"]`,
    );
    if (mainCartLineOriginal) return mainCartLineOriginal;

    const cartDrawerLineOriginal = document.querySelector(
      `#Cart-Drawer .cart-item-line-original[data-key="${itemKey}"]`,
    );
    if (cartDrawerLineOriginal) return cartDrawerLineOriginal;

    return this.getLineOriginalElements(itemKey)[0] || null;
  },

  getLineTotalWrappersByKey(itemKey) {
    if (!itemKey) return [];
    return Array.from(
      document.querySelectorAll(`.cart-item-total-price[data-key="${itemKey}"]`),
    );
  },

  getManagedLineTotalWrapper(itemKey) {
    if (!itemKey) return null;
    const mainCartWrapper = document.querySelector(
      `#Main-Cart .cart-item-total-price[data-key="${itemKey}"]`,
    );
    if (mainCartWrapper) return mainCartWrapper;

    const cartDrawerWrapper = document.querySelector(
      `#Cart-Drawer .cart-item-total-price[data-key="${itemKey}"]`,
    );
    if (cartDrawerWrapper) return cartDrawerWrapper;

    return this.getLineTotalWrappersByKey(itemKey)[0] || null;
  },

  rememberLineVOUnitPrice(itemKey, unitPriceCents) {
    if (!itemKey) return;
    if (typeof unitPriceCents !== "number" || unitPriceCents <= 0) return;
    this.lineVOUnitPriceCache.set(itemKey, unitPriceCents);
  },

  rememberLineBaseUnitPrice(itemKey, unitPriceCents) {
    if (!itemKey) return;
    if (typeof unitPriceCents !== "number" || unitPriceCents <= 0) return;
    this.lineBaseUnitPriceCache.set(itemKey, unitPriceCents);
  },

  getLineVOAmountsFromDom(itemKey, quantity) {
    const wrapper = this.getManagedLineTotalWrapper(itemKey);
    if (!wrapper) return null;

    const hasUnitAttr = wrapper.hasAttribute("data-vo-unit-price-cents");
    const hasLineAttr = wrapper.hasAttribute("data-vo-line-price-cents");
    if (!hasUnitAttr && !hasLineAttr) return null;

    const safeQuantity = this.normalizeQuantity(quantity);
    const parsedUnitPrice = this.parseCents(wrapper.dataset.voUnitPriceCents);
    const parsedLinePrice = this.parseCents(wrapper.dataset.voLinePriceCents);
    const unitPriceCents =
      parsedUnitPrice != null
        ? parsedUnitPrice
        : parsedLinePrice != null
          ? Math.round(parsedLinePrice / safeQuantity)
          : null;
    const linePriceCents =
      parsedLinePrice != null
        ? parsedLinePrice
        : unitPriceCents != null
          ? unitPriceCents * safeQuantity
          : null;

    if (unitPriceCents == null && linePriceCents == null) return null;

    this.rememberLineVOUnitPrice(itemKey, unitPriceCents);

    return {
      unitPriceCents: unitPriceCents || 0,
      linePriceCents: linePriceCents || 0,
    };
  },

  getLineVOAmounts(item) {
    const safeQuantity = this.normalizeQuantity(item?.quantity);
    const domAmounts = this.getLineVOAmountsFromDom(item?.key, safeQuantity);
    if (domAmounts) return domAmounts;

    const itemVoPrice = this.calculateItemVOPrice(item);
    if (itemVoPrice > 0) {
      this.rememberLineVOUnitPrice(item?.key, itemVoPrice);
      return {
        unitPriceCents: itemVoPrice,
        linePriceCents: itemVoPrice * safeQuantity,
      };
    }

    const cachedUnitPrice = item?.key
      ? this.lineVOUnitPriceCache.get(item.key)
      : null;
    if (typeof cachedUnitPrice === "number" && cachedUnitPrice > 0) {
      return {
        unitPriceCents: cachedUnitPrice,
        linePriceCents: cachedUnitPrice * safeQuantity,
      };
    }

    return {
      unitPriceCents: 0,
      linePriceCents: 0,
    };
  },

  getBaseLinePriceFromDom(itemKey, quantity) {
    const wrapper = this.getManagedLineTotalWrapper(itemKey);
    if (!wrapper) return null;

    const hasUnitAttr = wrapper.hasAttribute("data-base-unit-price-cents");
    const hasLineAttr = wrapper.hasAttribute("data-base-line-price-cents");
    if (!hasUnitAttr && !hasLineAttr) return null;

    const safeQuantity = this.normalizeQuantity(quantity);
    const parsedUnitPrice = this.parseCents(wrapper.dataset.baseUnitPriceCents);
    const parsedLinePrice = this.parseCents(wrapper.dataset.baseLinePriceCents);
    const unitPriceCents =
      parsedUnitPrice != null
        ? parsedUnitPrice
        : parsedLinePrice != null
          ? Math.round(parsedLinePrice / safeQuantity)
          : null;
    const linePriceCents =
      unitPriceCents != null
        ? unitPriceCents * safeQuantity
        : parsedLinePrice != null
          ? parsedLinePrice
          : null;

    if (unitPriceCents == null && linePriceCents == null) return null;

    this.rememberLineBaseUnitPrice(itemKey, unitPriceCents);

    return {
      unitPriceCents: unitPriceCents || 0,
      linePriceCents: linePriceCents || 0,
    };
  },

  getBaseLinePrice(item) {
    const safeQuantity = this.normalizeQuantity(item?.quantity);
    const domAmounts = this.getBaseLinePriceFromDom(item?.key, safeQuantity);
    if (domAmounts) {
      return domAmounts.linePriceCents;
    }

    const cachedUnitPrice = item?.key
      ? this.lineBaseUnitPriceCache.get(item.key)
      : null;
    if (typeof cachedUnitPrice === "number" && cachedUnitPrice > 0) {
      return cachedUnitPrice * safeQuantity;
    }

    const originalPrice = this.parseCents(item?.original_price);
    if (originalPrice == null || originalPrice <= 0) return 0;
    return originalPrice * safeQuantity;
  },

  getLinePriceState(item) {
    const voAmounts = this.getLineVOAmounts(item);
    const baseLinePriceCents = this.getBaseLinePrice(item);
    const originalLinePriceCents = this.parseCents(item?.original_line_price) || 0;
    const finalLinePriceCents = this.parseCents(item?.final_line_price);
    const inferredIncludedVOPriceCents =
      voAmounts.linePriceCents > 0 && baseLinePriceCents > 0
        ? Math.max(
          0,
          Math.min(
            voAmounts.linePriceCents,
            originalLinePriceCents - baseLinePriceCents,
          ),
        )
        : 0;
    const missingVOPriceCents = Math.max(
      0,
      voAmounts.linePriceCents - inferredIncludedVOPriceCents,
    );
    const alreadyIncludesVO =
      voAmounts.linePriceCents > 0 && missingVOPriceCents <= 1;
    const originalLineTotalCents = originalLinePriceCents + missingVOPriceCents;
    const finalLineTotalCents =
      typeof finalLinePriceCents === "number" && finalLinePriceCents > 0
        ? finalLinePriceCents + missingVOPriceCents
        : originalLineTotalCents;

    return {
      voLinePriceCents: voAmounts.linePriceCents,
      missingVOPriceCents,
      baseLinePriceCents,
      originalLinePriceCents,
      originalLineTotalCents,
      finalLineTotalCents,
      alreadyIncludesVO,
    };
  },

  getLineDiscountPlaceholders(itemKey) {
    if (!itemKey) return [];
    return Array.from(
      document.querySelectorAll(
        `.dr-stacked-discounts[data-item-key="${itemKey}"]`,
      ),
    );
  },

  getManagedLineDiscountPlaceholder(itemKey) {
    if (!itemKey) return null;
    const mainCartPlaceholder = document.querySelector(
      `#Main-Cart .dr-stacked-discounts[data-item-key="${itemKey}"]`,
    );
    if (mainCartPlaceholder) return mainCartPlaceholder;

    const cartDrawerPlaceholder = document.querySelector(
      `#Cart-Drawer .dr-stacked-discounts[data-item-key="${itemKey}"]`,
    );
    if (cartDrawerPlaceholder) return cartDrawerPlaceholder;

    return this.getLineDiscountPlaceholders(itemKey)[0] || null;
  },

  resetLineItemTotals(item) {
    if (!item?.key) return;

    const lineFinalEls = this.getLineFinalElements(item.key);
    if (!lineFinalEls.length) return;

    const lineOriginalEls = this.getLineOriginalElements(item.key);
    const linePriceState = this.getLinePriceState(item);
    const originalLineTotal = linePriceState.originalLineTotalCents;
    const correctedLineTotal = linePriceState.finalLineTotalCents;
    const formattedFinalTotal = this.formatMoney(correctedLineTotal);
    const formattedOriginalTotal = this.formatMoney(originalLineTotal);

    lineFinalEls.forEach((lineFinalEl) => {
      if (lineFinalEl.textContent.trim() !== formattedFinalTotal) {
        lineFinalEl.textContent = formattedFinalTotal;
      }
    });

    lineOriginalEls.forEach((lineOriginalEl) => {
      if (lineOriginalEl.textContent.trim() !== formattedOriginalTotal) {
        lineOriginalEl.textContent = formattedOriginalTotal;
      }
      lineOriginalEl.style.display = "none";
    });

    this.queueLineVisualReveal(item.key);
  },

  getRenderedLineDiscountByKey(itemKey) {
    if (!itemKey) return 0;
    const placeholder = this.getManagedLineDiscountPlaceholder(itemKey);
    if (!placeholder) return 0;

    let totalDiscount = 0;
    placeholder.querySelectorAll(".discount-item").forEach((discountItem) => {
      const cents = this.parseCents(discountItem.dataset.discountCents);
      if (typeof cents === "number" && cents > 0) {
        totalDiscount += cents;
      }
    });
    return totalDiscount;
  },

  scheduleLineVisualGlobalFailSafe() {
    if (this.lineVisualGlobalFailSafeTimer) {
      clearTimeout(this.lineVisualGlobalFailSafeTimer);
    }

    this.lineVisualGlobalFailSafeTimer = setTimeout(() => {
      document
        .querySelectorAll(
          ".main-cart-content .cart-item-total-price.is-visual-pending",
        )
        .forEach((wrapper) => {
          wrapper.classList.remove("is-visual-pending");
          wrapper.classList.add("is-visual-ready");
        });

      this.isVisualQuietReady = true;
      this.lineVisualPendingRevealKeys.clear();
    }, this.LINE_VISUAL_FAILSAFE_MS);
  },

  armLineVisualQuietWindow() {
    this.isVisualQuietReady = false;
    if (this.lineVisualQuietTimer) {
      clearTimeout(this.lineVisualQuietTimer);
    }

    this.lineVisualQuietTimer = setTimeout(() => {
      this.isVisualQuietReady = true;
      this.flushPendingLineVisualReveals();
    }, this.LINE_VISUAL_QUIET_MS);
  },

  flushPendingLineVisualReveals() {
    if (!this.isVisualQuietReady) return;
    if (!this.lineVisualPendingRevealKeys.size) return;

    Array.from(this.lineVisualPendingRevealKeys).forEach((itemKey) => {
      this.lineVisualPendingRevealKeys.delete(itemKey);

      const state = this.lineVisualState.get(itemKey);
      if (!state || !state.signature) {
        this.markLineVisualReady(itemKey);
        return;
      }

      const latestSignature = this.buildLineVisualSignature(itemKey);
      if (!latestSignature) {
        this.markLineVisualReady(itemKey);
        return;
      }

      if (latestSignature === state.signature) {
        this.markLineVisualReady(itemKey);
      } else {
        this.queueLineVisualReveal(itemKey);
      }
    });
  },

  startLineVisualQuietObserver() {
    if (this.lineVisualMutationObserver) return;

    const mainCartRoot = document.getElementById("Main-Cart");
    if (!mainCartRoot) return;

    this.lineVisualMutationObserver = new MutationObserver((mutations) => {
      const hasRelevantMutation = mutations.some((mutation) => {
        if (mutation.type === "childList") return true;
        if (mutation.type === "characterData") return true;
        return false;
      });
      if (!hasRelevantMutation) return;

      if (this.lineTotalsLoadingActive) {
        this.syncLineTotalsLoadingState();
      }

      this.armLineVisualQuietWindow();
      this.scheduleLineVisualGlobalFailSafe();
    });

    this.lineVisualMutationObserver.observe(mainCartRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  },

  initLineVisualGuard() {
    this.startLineVisualQuietObserver();
    this.armLineVisualQuietWindow();
    this.scheduleLineVisualGlobalFailSafe();
  },

  buildLineVisualSignature(itemKey) {
    const lineFinalEl = this.getManagedLineFinalElement(itemKey);
    if (!lineFinalEl) return "";

    const lineOriginalEl = this.getManagedLineOriginalElement(itemKey);
    const placeholder = this.getManagedLineDiscountPlaceholder(itemKey);

    const finalText = lineFinalEl.textContent.trim();
    const originalText = lineOriginalEl ? lineOriginalEl.textContent.trim() : "";
    const originalVisible =
      lineOriginalEl && lineOriginalEl.style.display !== "none" ? "1" : "0";
    const tagText = placeholder
      ? Array.from(placeholder.querySelectorAll(".discount-item"))
        .map((row) => row.textContent.replace(/\s+/g, " ").trim())
        .join("|")
      : "";

    return `${finalText}__${originalText}__${originalVisible}__${tagText}`;
  },

  markLineVisualReady(itemKey) {
    const lineFinalEl = this.getManagedLineFinalElement(itemKey);
    const wrapper = lineFinalEl?.closest(".cart-item-total-price");
    if (wrapper && wrapper.classList.contains("is-visual-pending")) {
      wrapper.classList.remove("is-visual-pending");
      wrapper.classList.add("is-visual-ready");
    }

    const state = this.lineVisualState.get(itemKey);
    if (!state) return;

    if (state.stableTimer) {
      clearTimeout(state.stableTimer);
    }
    this.lineVisualPendingRevealKeys.delete(itemKey);
    this.lineVisualState.delete(itemKey);
  },

  queueLineVisualReveal(itemKey) {
    if (!itemKey) return;

    const lineFinalEl = this.getManagedLineFinalElement(itemKey);
    if (!lineFinalEl) return;

    const wrapper = lineFinalEl.closest(".cart-item-total-price");
    if (!wrapper) return;

    // Only gate the main-cart visual flow; drawer remains unchanged.
    const isManagedNode =
      wrapper.classList.contains("is-visual-pending") ||
      wrapper.classList.contains("is-visual-ready");
    if (!isManagedNode) return;

    this.scheduleLineVisualGlobalFailSafe();

    const signature = this.buildLineVisualSignature(itemKey);
    if (!signature) {
      this.markLineVisualReady(itemKey);
      return;
    }

    const state = this.lineVisualState.get(itemKey) || {};
    state.signature = signature;

    if (state.stableTimer) {
      clearTimeout(state.stableTimer);
    }

    state.stableTimer = setTimeout(() => {
      const latestSignature = this.buildLineVisualSignature(itemKey);
      if (latestSignature !== state.signature) {
        this.queueLineVisualReveal(itemKey);
        return;
      }

      if (!this.isVisualQuietReady) {
        this.lineVisualPendingRevealKeys.add(itemKey);
        return;
      }

      this.markLineVisualReady(itemKey);
    }, this.LINE_VISUAL_STABLE_MS);

    this.lineVisualState.set(itemKey, state);
  },

  updateLineItemTotals(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return;

    cartData.items.forEach((item) => {
      const lineFinalEls = this.getLineFinalElements(item.key);
      if (!lineFinalEls.length) return;

      const lineOriginalEls = this.getLineOriginalElements(item.key);
      const linePriceState = this.getLinePriceState(item);
      const originalLineTotal = linePriceState.originalLineTotalCents;
      const renderedDiscount = this.getRenderedLineDiscountByKey(item.key);
      const fallbackDiscount = this.parseCents(item.total_discount) || 0;
      const appliedDiscount =
        renderedDiscount > 0 ? renderedDiscount : fallbackDiscount;

      let correctedLineTotal;
      if (appliedDiscount > 0) {
        correctedLineTotal = Math.max(0, originalLineTotal - appliedDiscount);
      } else {
        correctedLineTotal = linePriceState.finalLineTotalCents;
      }

      const formattedOriginalTotal = this.formatMoney(originalLineTotal);
      const formattedFinalTotal = this.formatMoney(correctedLineTotal);
      const showOriginal = correctedLineTotal < originalLineTotal;

      lineFinalEls.forEach((lineFinalEl) => {
        if (lineFinalEl.textContent.trim() !== formattedFinalTotal) {
          lineFinalEl.textContent = formattedFinalTotal;
        }
      });

      lineOriginalEls.forEach((lineOriginalEl) => {
        if (lineOriginalEl.textContent.trim() !== formattedOriginalTotal) {
          lineOriginalEl.textContent = formattedOriginalTotal;
        }
        lineOriginalEl.style.display = showOriginal ? "" : "none";
      });

      this.queueLineVisualReveal(item.key);
    });
  },

  // sale 图标 SVG（与 Liquid icon-sets 的 sale 图标一致）
  SALE_ICON_SVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="16" height="16" fill="none"><path d="M25.4358 12.6406L23.6114 10.8162C23.2993 10.5041 23.0472 9.89197 23.0472 9.45986V6.86721C23.0472 5.81095 22.183 4.94673 21.1268 4.94673H18.5461C18.114 4.94673 17.5018 4.69467 17.1898 4.38259L15.3653 2.55814C14.6211 1.81395 13.3968 1.81395 12.6526 2.55814L10.8042 4.38259C10.5041 4.69467 9.89197 4.94673 9.44785 4.94673H6.86721C5.81095 4.94673 4.94673 5.81095 4.94673 6.86721V9.44785C4.94673 9.87996 4.69467 10.4921 4.38259 10.8042L2.55814 12.6286C1.81395 13.3728 1.81395 14.5971 2.55814 15.3413L4.38259 17.1658C4.69467 17.4778 4.94673 18.09 4.94673 18.5221V21.1027C4.94673 22.159 5.81095 23.0232 6.86721 23.0232H9.44785C9.87996 23.0232 10.4921 23.2753 10.8042 23.5874L12.6286 25.4118C13.3728 26.156 14.5971 26.156 15.3413 25.4118L17.1658 23.5874C17.4778 23.2753 18.09 23.0232 18.5221 23.0232H21.1027C22.159 23.0232 23.0232 22.159 23.0232 21.1027V18.5221C23.0232 18.09 23.2753 17.4778 23.5874 17.1658L25.4118 15.3413C26.192 14.6091 26.192 13.3848 25.4358 12.6406ZM9.19579 10.3961C9.19579 9.73593 9.73593 9.19579 10.3961 9.19579C11.0563 9.19579 11.5964 9.73593 11.5964 10.3961C11.5964 11.0563 11.0683 11.5964 10.3961 11.5964C9.73593 11.5964 9.19579 11.0563 9.19579 10.3961ZM11.0322 18.234C10.8522 18.4141 10.6241 18.4981 10.3961 18.4981C10.168 18.4981 9.93998 18.4141 9.75993 18.234C9.41185 17.8859 9.41185 17.3098 9.75993 16.9617L16.9617 9.75993C17.3098 9.41185 17.8859 9.41185 18.234 9.75993C18.5821 10.108 18.5821 10.6842 18.234 11.0322L11.0322 18.234ZM17.5979 18.7982C16.9257 18.7982 16.3856 18.258 16.3856 17.5979C16.3856 16.9377 16.9257 16.3976 17.5859 16.3976C18.246 16.3976 18.7862 16.9377 18.7862 17.5979C18.7862 18.258 18.258 18.7982 17.5979 18.7982Z" fill="currentColor"/></svg>',

  /**
   * 同步修正行级折扣标签：统一渲染到 dr-stacked 占位标签
   */
  updateLineDiscountTags(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) {
      // console.log('%c[折扣调试] 未执行: cartData无效或无items', 'color:red;font-weight:bold', cartData);
      return;
    }

    // console.log('%c[折扣调试] 开始计算 (' + cartData.items.length + '个商品)', 'color:#0af;font-weight:bold');
    cartData.items.forEach(item => {
      const placeholders = this.getLineDiscountPlaceholders(item.key);
      if (!placeholders.length) return;

      const linePriceState = this.getLinePriceState(item);
      const voLineTotal = linePriceState.voLinePriceCents;
      const originalLineTotal = linePriceState.originalLineTotalCents;

      // console.log('[商品]', item.title || item.key, '| VO附加=$' + (voLineTotal / 100).toFixed(2) + ' | 原价=$' + (item.original_line_price / 100).toFixed(2));
      (item.line_level_discount_allocations || []).forEach(a => {
        // console.log('  [折扣]', a.discount_application?.title, '| type=' + a.discount_application?.value_type, '| value=' + a.discount_application?.value, '| Shopify金额=$' + (a.amount / 100).toFixed(2));
      });

      // 无折扣
      if (!item.line_level_discount_allocations ||
        item.line_level_discount_allocations.length === 0) {
        placeholders.forEach(placeholder => {
          placeholder.innerHTML = '';
          placeholder.style.display = 'none';
        });
        this.resetLineItemTotals(item);
        return;
      }

      let html = '';
      item.line_level_discount_allocations.forEach(alloc => {
        const app = alloc.discount_application;
        let correctedAmount;

        if (voLineTotal > 0 && app && app.value_type === 'percentage') {
          correctedAmount = Math.floor(originalLineTotal * parseFloat(app.value) / 100);
          // console.log('  ✅ 重算后=$' + (correctedAmount / 100).toFixed(2), '(含VO总价=$' + (fullOriginal / 100).toFixed(2) + ', 比例=' + app.value + '%)');
        } else {
          correctedAmount = alloc.amount;
          // console.log('  ⚠️ 使用原值=$' + (alloc.amount / 100).toFixed(2), '(无VO或非百分比)');
        }

        const title = app ? (app.title || '') : '';
        const formatted = this.formatMoney(correctedAmount);
        html += '<li class="discount-item" data-discount-cents="' + correctedAmount + '">' +
          this.SALE_ICON_SVG +
          '<span>' + title + ' (-' + formatted + ')</span>' +
          '</li>';
      });

      placeholders.forEach(placeholder => {
        placeholder.innerHTML = html;
        placeholder.style.display = '';
      });
    });
  },

  calculateCorrectedDiscount(discountPercent, originalTotal, voOptionsTotal) {
    const fullTotal = originalTotal + voOptionsTotal;
    return Math.floor(fullTotal * discountPercent / 100);
  },

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  },

  updateDiscountDisplay(cartData) {
    if (!cartData) return;

    this.updateLineDiscountTags(cartData);
    this.updateLineItemTotals(cartData);
    const voOptionsTotal = this.calculateVOOptionsTotal(cartData.items);

    const discountItems = document.querySelectorAll('.discount-item[data-discount-type="percentage"]');
    if (!discountItems.length) {
      this.updateEstimatedTotal(cartData, voOptionsTotal);
      return;
    }

    discountItems.forEach(item => {
      const discountValue = parseFloat(item.dataset.discountValue);
      if (isNaN(discountValue)) return;

      if (voOptionsTotal > 0) {
        const correctedDiscount = this.calculateCorrectedDiscount(
          discountValue,
          cartData.original_total_price,
          voOptionsTotal
        );

        const discountAmountEl = item.querySelector('.discount-amount');
        if (discountAmountEl) {
          discountAmountEl.textContent = this.formatMoney(correctedDiscount);
          discountAmountEl.dataset.calculatedDiscount = correctedDiscount;
        }
      }
    });

    this.updateEstimatedTotal(cartData, voOptionsTotal);
  },

  buildSignature(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return "";
    const itemSig = cartData.items
      .map((item) => `${item.key}:${item.quantity}:${item.final_line_price || 0}`)
      .join("|");
    return `${cartData.item_count || 0}:${cartData.total_price || 0}:${itemSig}`;
  },

  applyStableUpdate(cartData) {
    if (!cartData) return;
    const signature = this.buildSignature(cartData);
    if (signature && signature === this.lastAppliedSignature) return;

    const run = () => {
      this.updateDiscountDisplay(cartData);
      this.lastAppliedSignature = signature;
      this.releaseLineTotalsLoading();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  },

  queueApply(cartData) {
    this.pendingCartData = cartData || this.pendingCartData;
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => {
      const data = this.pendingCartData;
      this.pendingCartData = null;
      this.syncTimer = null;
      this.applyStableUpdate(data);
    }, 80);
  },

  /**
   * 更新 Estimated total 显示
   */
  updateEstimatedTotal(cartData, voOptionsTotal) {
    if (!voOptionsTotal) {
      voOptionsTotal = this.calculateVOOptionsTotal(cartData.items);
    }

    const totalEl = document.querySelector('.vo-estimated-total');
    if (!totalEl) return;

    let voDiscountTotal = 0;
    if (voOptionsTotal > 0 && cartData.cart_level_discount_applications) {
      cartData.cart_level_discount_applications.forEach(discount => {
        if (discount.value_type === 'percentage') {
          voDiscountTotal += Math.floor(voOptionsTotal * discount.value / 100);
        }
      });
    }

    const estimatedTotal = cartData.total_price + voOptionsTotal - voDiscountTotal;
    totalEl.textContent = this.formatMoney(estimatedTotal);
  },

  async syncFromCartApi(force = false) {
    const now = Date.now();
    if (!force && now - this.lastSyncAt < 300) return;
    if (this.syncInFlight) return this.syncInFlight;

    this.lastSyncAt = now;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const url = `${root}cart.js`;

    this.syncInFlight = fetch(url, { credentials: "same-origin" })
      .then((res) => {
        if (!res.ok) throw new Error(`Cart sync failed: ${res.status}`);
        return res.json();
      })
      .then((cartData) => {
        this.queueApply(cartData);
      })
      .catch(() => { })
      .finally(() => {
        this.syncInFlight = null;
      });

    return this.syncInFlight;
  }
};

if (typeof webvista !== 'undefined' && webvista.subscribe) {
  webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    VODiscountFix.armLineVisualQuietWindow();
    VODiscountFix.scheduleLineVisualGlobalFailSafe();
    if (event.cartData) {
      VODiscountFix.queueApply(event.cartData);
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    VODiscountFix.initLineVisualGuard();
    VODiscountFix.syncFromCartApi(true);
  });
} else {
  VODiscountFix.initLineVisualGuard();
  VODiscountFix.syncFromCartApi(true);
}

const cartDrawer = document.getElementById("Cart-Drawer");
if (cartDrawer) {
  const drawerObserver = new MutationObserver(() => {
    if (cartDrawer.hasAttribute("open")) {
      VODiscountFix.syncFromCartApi(true);
    }
  });
  drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ["open"] });
}

const CartCheckoutWaitGuard = {
  POLL_INTERVAL_MS: 300,
  INITIAL_DELAY_MS: 120,
  SUMMARY_QUIET_MS: 900,
  SUMMARY_FAILSAFE_MS: 8000,
  SUMMARY_SELECTORS: [
    ".docapp-cart-with-coupon-summary",
    ".docapp-widget[class*='docapp-cart-with-coupon-summary']",
  ],
  pollTimer: null,
  requestId: 0,
  waiting: true,
  initialized: false,
  buttonObserver: null,
  summaryObserver: null,
  observedSummaryRoot: null,
  summaryQuietTimer: null,
  summaryFailSafeTimer: null,
  summaryQuietReady: false,

  // ── Checkout-unlock fix ───────────────────────────────────────────────────
  HARD_RELEASE_MS: 2000,
  hotfixReleased: false,
  hotfixReleaseTimer: null,

  scheduleHardRelease() {
    if (this.hotfixReleaseTimer) return;
    this.hotfixReleaseTimer = setTimeout(() => {
      this.hotfixReleased = true;
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.applyWaitingState(false);
    }, this.HARD_RELEASE_MS);
  },

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.startButtonObserver();
    this.applyWaitingState(true);
    this.requestSync();
    this.scheduleHardRelease();

    document.addEventListener("docapp-discount-applied", () => {
      if (this.waiting) {
        this.scheduleCheck(this.requestId, 80);
      }
    });

    if (typeof webvista !== "undefined" && webvista.subscribe) {
      webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.requestSync();
      });
    }
  },

  hold() {
    this.requestId += 1;
    clearTimeout(this.pollTimer);
    this.resetSummaryQuietState();
    this.applyWaitingState(true);
    this.scheduleSummaryFailSafe(this.requestId);
  },

  requestSync() {
    this.hold();
    this.ensureSummaryObserver();
    this.armSummaryQuietWindow();
    this.scheduleSummaryFailSafe(this.requestId);
    this.scheduleCheck(this.requestId, this.INITIAL_DELAY_MS);
  },

  startButtonObserver() {
    if (this.buttonObserver || !document.body) return;

    this.buttonObserver = new MutationObserver((mutations) => {
      const shouldSync = mutations.some((mutation) => {
        if (mutation.type !== "childList") return false;

        return [...mutation.addedNodes].some((node) => this.hasCheckoutButton(node));
      });

      if (!shouldSync) return;
      this.applyWaitingState(this.waiting);
    });

    this.buttonObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  },

  scheduleCheck(currentRequestId, delay = this.POLL_INTERVAL_MS) {
    clearTimeout(this.pollTimer);
    this.pollTimer = setTimeout(() => this.checkReady(currentRequestId), delay);
  },

  checkReady(currentRequestId) {
    if (currentRequestId !== this.requestId) return;
    this.ensureSummaryObserver();

    if (this.isCheckoutReady()) {
      this.applyWaitingState(false);
      return;
    }

    this.scheduleCheck(currentRequestId);
  },

  isCheckoutReady() {
    return this.isSummaryReady() && this.isSummaryQuiet() && this.isDependentFlowQuiet();
  },

  isSummaryReady() {
    const summary = this.findSummaryRoot();
    if (!summary || !this.isVisible(summary)) return false;

    const text = (summary.textContent || "").replace(/\s+/g, " ").trim();
    const descendantCount = summary.querySelectorAll("*").length;
    const hasControls = Boolean(summary.querySelector("input, button"));
    const signalCount = summary.querySelectorAll(
      "[class*='coupon'], [class*='discount'], [class*='subtotal'], [class*='total']",
    ).length;
    const hasTotalsText = /(subtotal|total|discount|shipping)/i.test(text);

    return hasControls && descendantCount >= 6 && (
      signalCount >= 2 ||
      hasTotalsText ||
      text.length >= 40
    );
  },

  isSummaryQuiet() {
    return this.summaryQuietReady;
  },

  isDependentFlowQuiet() {
    if (typeof VODiscountFix !== "undefined") {
      if (VODiscountFix.lineTotalsLoadingActive) return false;
      if (!VODiscountFix.isVisualQuietReady) return false;
      if ((VODiscountFix.lineVisualPendingRevealKeys?.size || 0) > 0) return false;
      if ((VODiscountFix.lineVisualState?.size || 0) > 0) return false;
    }

    if (typeof DrStackedDiscountTags !== "undefined") {
      if (DrStackedDiscountTags.pendingRemovalCodes) return false;
      if (DrStackedDiscountTags._updating) return false;
    }

    return true;
  },

  resetSummaryQuietState() {
    this.summaryQuietReady = false;
    clearTimeout(this.summaryQuietTimer);
    clearTimeout(this.summaryFailSafeTimer);
    this.summaryQuietTimer = null;
    this.summaryFailSafeTimer = null;
  },

  scheduleSummaryFailSafe(currentRequestId) {
    clearTimeout(this.summaryFailSafeTimer);
    this.summaryFailSafeTimer = setTimeout(() => {
      if (currentRequestId !== this.requestId) return;
      this.summaryQuietReady = true;
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.applyWaitingState(false);
    }, this.SUMMARY_FAILSAFE_MS);
  },

  armSummaryQuietWindow() {
    clearTimeout(this.summaryQuietTimer);
    this.summaryQuietReady = false;
    this.summaryQuietTimer = setTimeout(() => {
      this.summaryQuietReady = true;
    }, this.SUMMARY_QUIET_MS);
  },

  ensureSummaryObserver() {
    const nextRoot = this.findObservedSummaryRoot();
    if (nextRoot === this.observedSummaryRoot) return;

    if (this.summaryObserver) {
      this.summaryObserver.disconnect();
      this.summaryObserver = null;
    }

    this.observedSummaryRoot = nextRoot;
    if (!nextRoot) return;

    this.summaryObserver = new MutationObserver((mutations) => {
      const hasRelevantMutation = mutations.some((mutation) =>
        this.isRelevantSummaryMutation(mutation),
      );

      if (!hasRelevantMutation) return;
      this.armSummaryQuietWindow();
    });

    this.summaryObserver.observe(nextRoot, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden", "aria-busy", "data-state", "open"],
    });

    this.armSummaryQuietWindow();
  },

  findObservedSummaryRoot() {
    const summary = this.findSummaryRoot();
    if (!summary) return null;

    return summary.closest(".cart-details-footer") || summary;
  },

  isRelevantSummaryMutation(mutation) {
    if (!mutation) return false;

    if (mutation.type === "childList") {
      const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
      return nodes.some((node) => !this.isCheckoutButtonNode(node));
    }

    const target = mutation.target instanceof Node ? mutation.target : null;
    if (!target) return false;

    return !this.isCheckoutButtonNode(target);
  },

  isCheckoutButtonNode(node) {
    if (!node) return false;

    if (node instanceof Element) {
      return (
        node.matches("[data-cart-checkout-button]") ||
        Boolean(node.closest("[data-cart-checkout-button]")) ||
        Boolean(node.querySelector?.("[data-cart-checkout-button]"))
      );
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return this.isCheckoutButtonNode(node.parentElement);
    }

    return false;
  },

  findSummaryRoot() {
    for (const selector of this.SUMMARY_SELECTORS) {
      const candidates = document.querySelectorAll(selector);
      for (const candidate of candidates) {
        if (this.isVisible(candidate)) {
          return candidate;
        }
      }
    }

    return null;
  },

  isVisible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }
    return element.getClientRects().length > 0;
  },

  hasCheckoutButton(node) {
    if (!(node instanceof Element)) return false;
    return (
      node.matches("[data-cart-checkout-button]") ||
      Boolean(node.querySelector("[data-cart-checkout-button]"))
    );
  },

  setMainCartFooterReady(ready) {
    document.querySelectorAll(".main-cart-content .cart-details-footer").forEach((footer) => {
      footer.classList.toggle("is-footer-ready", ready);
    });
  },

  applyWaitingState(waiting) {
    // Hotfix: once released for this session, never re-lock the checkout button.
    if (this.hotfixReleased && waiting) return;
    this.waiting = waiting;

    document.querySelectorAll("[data-cart-checkout-button]").forEach((button) => {
      button.classList.toggle("loading", waiting);

      if (waiting) {
        button.setAttribute("disabled", "disabled");
        button.setAttribute("aria-disabled", "true");
        button.setAttribute("aria-busy", "true");
      } else {
        button.removeAttribute("disabled");
        button.setAttribute("aria-disabled", "false");
        button.setAttribute("aria-busy", "false");
      }
    });
    this.setMainCartFooterReady(!waiting);
  },
};

/**
 * Dr Stacked 折扣标签渲染
 */
const DrStackedDiscountTags = {
  DISCOUNT_MAP: {
    'NEW10': { percent: 10, label: 'NEW10' },
    'SP15': { percent: 15, label: 'SP15' },
    'SP20': { percent: 20, label: 'SP20' },
    'NEW30': { percent: 30, label: 'New30' },
  },
  lastKnownCodes: [],
  pendingRemovalCodes: null,
  removalTransitionTimer: null,

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  },

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  },

  normalizeCode(value) {
    return String(value || '').trim().toUpperCase();
  },

  getNormalizedCodes(codes) {
    return this.collectCodeInfoList(Array.isArray(codes) ? codes : []).map((info) => info.code);
  },

  haveCodesShrunk(nextCodes) {
    if (!Array.isArray(nextCodes) || !Array.isArray(this.lastKnownCodes)) return false;
    if (!this.lastKnownCodes.length || nextCodes.length >= this.lastKnownCodes.length) {
      return false;
    }

    const nextSet = new Set(nextCodes);
    return this.lastKnownCodes.some((code) => !nextSet.has(code));
  },

  beginCodeRemovalTransition(nextCodes) {
    this.pendingRemovalCodes = Array.isArray(nextCodes) ? [...nextCodes] : [];

    if (this.removalTransitionTimer) {
      clearTimeout(this.removalTransitionTimer);
    }
    this.removalTransitionTimer = setTimeout(() => {
      this.endCodeRemovalTransition();
      if (typeof VODiscountFix !== "undefined" && VODiscountFix.syncFromCartApi) {
        VODiscountFix.syncFromCartApi(true);
      }
    }, 7000);

    if (typeof VODiscountFix !== "undefined") {
      if (VODiscountFix.setLineTotalsLoading) {
        VODiscountFix.setLineTotalsLoading(true);
      }
      if (VODiscountFix.syncFromCartApi) {
        VODiscountFix.syncFromCartApi(true);
      }
    }
  },

  endCodeRemovalTransition(options = {}) {
    const { refresh = true } = options;
    const stabilizedCodes = Array.isArray(this.pendingRemovalCodes)
      ? [...this.pendingRemovalCodes]
      : null;

    if (this.removalTransitionTimer) {
      clearTimeout(this.removalTransitionTimer);
      this.removalTransitionTimer = null;
    }
    this.pendingRemovalCodes = null;

    if (typeof VODiscountFix !== "undefined" && VODiscountFix.releaseLineTotalsLoading) {
      VODiscountFix.releaseLineTotalsLoading(0);
    }

    if (refresh && stabilizedCodes) {
      this._doUpdate(stabilizedCodes);
    }
  },

  maybeBeginCodeRemovalTransition(rawCodes) {
    const normalizedCodes = this.getNormalizedCodes(rawCodes);
    if (!this.pendingRemovalCodes && this.haveCodesShrunk(normalizedCodes)) {
      this.beginCodeRemovalTransition(normalizedCodes);
    }
    this.lastKnownCodes = normalizedCodes;
    return normalizedCodes;
  },

  parseCents(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.round(Math.abs(value));
    }
    if (typeof value === 'string') {
      const parsed = Number(value.trim());
      if (Number.isFinite(parsed)) return Math.round(Math.abs(parsed));
    }
    return null;
  },

  normalizeQuantity(value) {
    return Math.max(1, parseInt(value, 10) || 1);
  },

  buildCodeInfo(rawCode) {
    const code = this.normalizeCode(rawCode);
    if (!code) return null;

    if (this.DISCOUNT_MAP[code]) {
      const mapped = this.DISCOUNT_MAP[code];
      return {
        code,
        label: mapped.label || code,
        percent: Number(mapped.percent) || 0,
      };
    }

    const m = code.match(/(\d+(?:\.\d+)?)/);
    return {
      code,
      label: code,
      percent: m ? parseFloat(m[1]) : 0,
    };
  },

  collectCodeInfoList(codes) {
    if (!Array.isArray(codes)) return [];

    const list = [];
    const seen = new Set();
    codes.forEach((raw) => {
      const info = this.buildCodeInfo(raw);
      if (!info || seen.has(info.code)) return;
      seen.add(info.code);
      list.push(info);
    });
    return list;
  },

  collectItemDiscountRows(item) {
    if (!item) return [];

    const sources = [
      item.applied_discounts,
      item.discounts,
      item.discount_allocations,
      item.line_level_discount_allocations,
    ];

    const rows = [];
    sources.forEach((source) => {
      if (!Array.isArray(source)) return;

      source.forEach((entry) => {
        if (!entry) return;

        const app = entry.discount_application || {};
        const rawTitle =
          app.title ||
          entry.title ||
          entry.code ||
          entry.discount_code ||
          entry.discount_title;

        const normalized = this.normalizeCode(rawTitle);
        if (!normalized) return;

        const amount =
          this.parseCents(entry.amount) ??
          this.parseCents(entry.total_allocated_amount) ??
          this.parseCents(entry.discount_amount);

        rows.push({
          code: normalized,
          label: String(rawTitle || normalized).trim(),
          amount,
        });
      });
    });

    if (!rows.length) return [];

    const merged = [];
    const mergedMap = new Map();

    rows.forEach((row) => {
      if (!mergedMap.has(row.code)) {
        mergedMap.set(row.code, merged.length);
        merged.push({ ...row });
        return;
      }

      const idx = mergedMap.get(row.code);
      const current = merged[idx];
      if ((current.amount == null || current.amount <= 0) && row.amount != null && row.amount > 0) {
        current.amount = row.amount;
      }
      if (!current.label && row.label) {
        current.label = row.label;
      }
    });

    return merged;
  },

  isCartStaleDuringCodeRemoval(cart, expectedCodes = this.pendingRemovalCodes) {
    if (!Array.isArray(cart?.items)) return true;

    const normalizedExpectedCodes = Array.isArray(expectedCodes)
      ? expectedCodes
      : this.getNormalizedCodes(expectedCodes);
    const expectedCodeSet = new Set(normalizedExpectedCodes);

    return cart.items.some((item) => {
      const totalDiscount = this.parseCents(item?.total_discount) || 0;
      const detailRows = this.collectItemDiscountRows(item);

      if (!expectedCodeSet.size) {
        return totalDiscount > 0 || detailRows.length > 0;
      }

      if (!detailRows.length) {
        return totalDiscount > 0;
      }

      return detailRows.some((row) => {
        const code = this.normalizeCode(row?.code || row?.label);
        return code && !expectedCodeSet.has(code);
      });
    });
  },

  matchRowByCode(rowCode, code) {
    if (!rowCode || !code) return false;
    if (rowCode === code) return true;
    return rowCode.includes(code) || code.includes(rowCode);
  },

  resolveOriginalLineAmount(item) {
    const linePriceState =
      typeof VODiscountFix !== "undefined" &&
      VODiscountFix &&
      typeof VODiscountFix.getLinePriceState === "function"
        ? VODiscountFix.getLinePriceState(item)
        : null;
    if (linePriceState && linePriceState.originalLineTotalCents > 0) {
      return linePriceState.originalLineTotalCents;
    }

    const originalLine = this.parseCents(item?.original_line_price);
    if (originalLine != null && originalLine > 0) return originalLine;

    const originalPrice = this.parseCents(item?.original_price);
    const quantity = this.normalizeQuantity(item?.quantity);
    if (originalPrice != null && originalPrice > 0) {
      return originalPrice * quantity;
    }

    const finalLine = this.parseCents(item?.final_line_price);
    const totalDiscount = this.parseCents(item?.total_discount);
    if (finalLine != null && totalDiscount != null) {
      return finalLine + totalDiscount;
    }

    return 0;
  },

  buildForcedSplitRows(item, codeInfoList, currentRows) {
    if (!item || !Array.isArray(codeInfoList) || codeInfoList.length < 2) {
      return null;
    }
    if (!Array.isArray(currentRows) || currentRows.length < 1) {
      return null;
    }

    const rowMap = new Map();
    currentRows.forEach((row) => {
      const normalized = this.normalizeCode(row?.code || row?.label);
      if (!normalized || rowMap.has(normalized)) return;
      rowMap.set(normalized, row);
    });

    const new10Info = codeInfoList.find((info) => info.code === 'NEW10');
    if (!new10Info) return null;

    const nonNewRows = currentRows.filter((row) => {
      const normalized = this.normalizeCode(row?.code || row?.label);
      return normalized && normalized !== 'NEW10';
    });
    if (nonNewRows.length !== 1) return null;

    const otherRow = nonNewRows[0];
    const otherCode = this.normalizeCode(otherRow?.code || otherRow?.label);
    const otherInfo = codeInfoList.find((info) => info.code === otherCode);
    if (!otherInfo) return null;

    const totalDiscount = this.parseCents(item?.total_discount) || 0;
    const originalLine = this.resolveOriginalLineAmount(item);
    if (totalDiscount <= 0 || originalLine <= 0) return null;

    const new10Percent = Number(new10Info.percent) || 10;
    const new10Amount = Math.floor((originalLine * new10Percent) / 100);
    if (new10Amount <= 0 || new10Amount >= totalDiscount) return null;

    const otherAmount = totalDiscount - new10Amount;
    if (otherAmount <= 0) return null;

    const hasVisibleNew10 = currentRows.some((row) => {
      const normalized = this.normalizeCode(row?.code || row?.label);
      return normalized === 'NEW10';
    });
    const discountRatio = (totalDiscount / originalLine) * 100;
    const otherPercent = Number(otherInfo.percent) || 0;
    if (!hasVisibleNew10) {
      if (otherPercent <= 0) return null;
      if (discountRatio <= otherPercent + 1) return null;
    }

    const existingNew10 = rowMap.get(new10Info.code);
    const existingOther = rowMap.get(otherInfo.code);
    const splitLooksComplete =
      existingNew10 &&
      existingOther &&
      typeof existingNew10.amount === 'number' &&
      existingNew10.amount > 0 &&
      typeof existingOther.amount === 'number' &&
      existingOther.amount > 0 &&
      Math.abs(existingNew10.amount - new10Amount) <= 1 &&
      Math.abs(existingOther.amount - otherAmount) <= 1;

    if (splitLooksComplete) return null;

    if (!hasVisibleNew10) {
      return [
        {
          code: otherRow.code || otherInfo.code,
          label: otherRow.label || otherInfo.label || otherInfo.code,
          amount: otherAmount,
        },
        {
          code: new10Info.code,
          label: new10Info.label || new10Info.code,
          amount: new10Amount,
        },
      ];
    }

    return currentRows.map((row) => {
      const normalized = this.normalizeCode(row?.code || row?.label);
      return {
        code: row.code,
        label: row.label,
        amount: normalized === new10Info.code
          ? new10Amount
          : normalized === otherInfo.code
            ? otherAmount
            : row.amount,
      };
    });
  },

  buildDiscountRows(item, codes) {
    const codeInfoList = this.collectCodeInfoList(codes);
    const detailRows = this.collectItemDiscountRows(item);
    const result = [];
    const used = new Set();

    codeInfoList.forEach((info) => {
      const rowIndex = detailRows.findIndex((row, idx) => (
        !used.has(idx) && this.matchRowByCode(row.code, info.code)
      ));

      if (rowIndex === -1) return;
      used.add(rowIndex);

      const row = detailRows[rowIndex];
      result.push({
        code: info.code,
        label: info.label || row.label || info.code,
        amount: row.amount,
      });
    });

    detailRows.forEach((row, idx) => {
      if (used.has(idx)) return;
      if (codeInfoList.length) return;
      result.push({
        code: row.code,
        label: row.label || row.code,
        amount: row.amount,
      });
    });

    if (!result.length) {
      const fallback = this.identifyCode(item, codes);
      if (fallback) {
        result.push({
          code: this.normalizeCode(fallback.label),
          label: fallback.label,
          amount: this.parseCents(item?.total_discount),
        });
      }
    }

    const forcedSplitRows = this.buildForcedSplitRows(item, codeInfoList, result);
    if (forcedSplitRows) {
      return forcedSplitRows;
    }

    // Use item-level detected discount tag count as the render gate.
    const itemDiscountTagCount = detailRows.length;
    if (itemDiscountTagCount < 2) {
      return result;
    }

    if (result.length < 2) {
      return result;
    }

    const represented = new Set();
    result.forEach((row) => {
      const normalized = this.normalizeCode(row.code || row.label);
      if (normalized) represented.add(normalized);
    });

    const missing = codeInfoList.filter((info) => !represented.has(info.code));
    const totalDiscount = this.parseCents(item?.total_discount) || 0;
    const originalLine = this.resolveOriginalLineAmount(item);
    const discountRatio = originalLine > 0 ? (totalDiscount / originalLine) * 100 : 0;
    const knownPercent = codeInfoList
      .filter((info) => represented.has(info.code))
      .reduce((sum, info) => sum + (Number(info.percent) || 0), 0);

    if (missing.length && totalDiscount > 0 && discountRatio > knownPercent + 1) {
      let remaining = totalDiscount;
      result.forEach((row) => {
        if (typeof row.amount === 'number' && row.amount > 0) {
          remaining -= row.amount;
        }
      });

      if (missing.length === 1 && remaining > 0) {
        result.push({
          code: missing[0].code,
          label: missing[0].label || missing[0].code,
          amount: remaining,
        });
      } else {
        missing.forEach((info) => {
          result.push({
            code: info.code,
            label: info.label || info.code,
            amount: null,
          });
        });
      }
    }

    return result;
  },

  identifyCode(item, codes) {
    if (!item.total_discount || item.total_discount <= 0) return null;
    if (!codes || codes.length === 0) return null;

    const upperCodes = codes.map(c => c.toUpperCase());

    const codeInfoList = upperCodes.map(c => {
      if (this.DISCOUNT_MAP[c]) return this.DISCOUNT_MAP[c];
      const m = c.match(/(\d+)/);
      return { percent: m ? parseInt(m[1], 10) : 0, label: c };
    });

    if (codeInfoList.length === 1) return codeInfoList[0];
    const originalLine = this.resolveOriginalLineAmount(item);
    if (originalLine <= 0) return codeInfoList[0];
    const ratio = (item.total_discount / originalLine) * 100;

    let best = null;
    let bestDiff = Infinity;
    for (const info of codeInfoList) {
      const diff = Math.abs(ratio - info.percent);
      if (diff < bestDiff) { bestDiff = diff; best = info; }
    }
    return bestDiff <= 5 ? best : codeInfoList[0];
  },

  syncRowsToCodeOrderAndAmounts(rows, item, codes) {
    if (!Array.isArray(rows) || rows.length < 1) return rows || [];

    const codeInfoList = this.collectCodeInfoList(codes);
    if (!codeInfoList.length) return rows;

    const rowMap = new Map();
    rows.forEach((row) => {
      const code = this.normalizeCode(row?.code || row?.label);
      if (!code || rowMap.has(code)) return;
      rowMap.set(code, { ...row, code });
    });

    let orderedRows = [];
    codeInfoList.forEach((info) => {
      const row = rowMap.get(info.code);
      if (!row) return;
      orderedRows.push({
        ...row,
        label: info.label || row.label || info.code,
      });
    });
    rows.forEach((row) => {
      const code = this.normalizeCode(row?.code || row?.label);
      if (!code) return;
      if (!orderedRows.some((existing) => existing.code === code)) {
        orderedRows.push({
          ...row,
          code,
        });
      }
    });

    if (orderedRows.length < 2) {
      const totalDiscount = this.parseCents(item?.total_discount) || 0;
      const originalLine = this.resolveOriginalLineAmount(item);

      // Handle transitional state after applying the second code:
      // right panel has two codes, but item rows may still contain one code briefly.
      if (orderedRows.length === 1 && codeInfoList.length === 2 && totalDiscount > 0 && originalLine > 0) {
        const existingCode = orderedRows[0].code;
        const existingInfo = codeInfoList.find((info) => info.code === existingCode);
        const missingInfo = codeInfoList.find((info) => info.code !== existingCode);
        const existingPercent = Number(existingInfo?.percent) || 0;
        const missingPercent = Number(missingInfo?.percent) || 0;
        const discountRatio = (totalDiscount / originalLine) * 100;

        if (missingInfo && missingPercent > 0 && discountRatio > existingPercent + 1) {
          orderedRows = codeInfoList.map((info) => {
            if (info.code === existingCode) return orderedRows[0];
            return {
              code: info.code,
              label: info.label || info.code,
              amount: null,
            };
          });
        }
      }

      if (orderedRows.length < 2) return orderedRows;
    }

    const allMatchedPercentCodes = orderedRows.every((row) => {
      const info = codeInfoList.find((codeInfo) => codeInfo.code === row.code);
      return info && Number(info.percent) > 0;
    });
    if (!allMatchedPercentCodes) return orderedRows;

    const totalDiscount = this.parseCents(item?.total_discount) || 0;
    const originalLine = this.resolveOriginalLineAmount(item);
    if (totalDiscount <= 0 || originalLine <= 0) return orderedRows;

    let remainingBase = originalLine;
    let remainingDiscount = totalDiscount;
    const recalculated = orderedRows.map((row, idx) => {
      if (idx === orderedRows.length - 1) {
        return {
          ...row,
          amount: remainingDiscount,
        };
      }

      const info = codeInfoList.find((codeInfo) => codeInfo.code === row.code);
      const percent = Number(info?.percent) || 0;
      const computed = Math.floor((remainingBase * percent) / 100);
      const amount = Math.max(0, Math.min(computed, remainingDiscount));
      remainingBase = Math.max(0, remainingBase - amount);
      remainingDiscount = Math.max(0, remainingDiscount - amount);

      return {
        ...row,
        amount,
      };
    });

    const sum = recalculated.reduce((total, row) => total + (this.parseCents(row.amount) || 0), 0);
    if (Math.abs(sum - totalDiscount) > 1) return orderedRows;
    return recalculated;
  },

  render(discountedCart, codes) {
    if (!discountedCart || !discountedCart.items) return;

    const placeholders = document.querySelectorAll('.dr-stacked-discounts');
    if (!placeholders.length) return;

    const itemMap = new Map();
    discountedCart.items.forEach(item => {
      if (item.key) itemMap.set(item.key, item);
    });

    this._updating = true;
    placeholders.forEach(ul => {
      const key = ul.dataset.itemKey;
      const item = itemMap.get(key);

      if (!item || !item.total_discount || item.total_discount <= 0) {
        ul.innerHTML = '';
        ul.style.display = 'none';
        if (item && typeof VODiscountFix !== 'undefined' && VODiscountFix.resetLineItemTotals) {
          VODiscountFix.resetLineItemTotals(item);
        }
        return;
      }

      const rows = this.buildDiscountRows(item, codes);
      if (!rows.length) {
        ul.innerHTML = '';
        ul.style.display = 'none';
        if (typeof VODiscountFix !== 'undefined' && VODiscountFix.resetLineItemTotals) {
          VODiscountFix.resetLineItemTotals(item);
        }
        return;
      }

      const uniqueRows = [];
      const rowSet = new Set();
      rows.forEach((row) => {
        const normalized = this.normalizeCode(row?.code || row?.label);
        const label = String(row?.label || row?.code || '').trim();
        if (!normalized || !label || rowSet.has(normalized)) return;
        rowSet.add(normalized);
        uniqueRows.push({
          label,
          amount: this.parseCents(row?.amount),
        });
      });

      if (!uniqueRows.length) {
        ul.innerHTML = '';
        ul.style.display = 'none';
        if (typeof VODiscountFix !== 'undefined' && VODiscountFix.resetLineItemTotals) {
          VODiscountFix.resetLineItemTotals(item);
        }
        return;
      }

      const displayRows = this.syncRowsToCodeOrderAndAmounts(uniqueRows, item, codes);
      ul.innerHTML = displayRows.map((row) => {
        const safeLabel = this.escapeHtml(row.label);
        const discountCents = (typeof row.amount === 'number' && row.amount > 0)
          ? row.amount
          : 0;
        const amountText = (typeof row.amount === 'number' && row.amount > 0)
          ? ' (-' + this.formatMoney(row.amount) + ')'
          : '';
        return (
          '<li class="discount-item" data-discount-cents="' + discountCents + '">' +
          '<span>\uD83C\uDFF7\uFE0F ' + safeLabel + amountText + '</span>' +
          '</li>'
        );
      }).join('');
      ul.style.display = '';

      // Re-sync line totals after final discount tags are rendered.
      // Some app scripts may overwrite line totals shortly after section updates.
      if (typeof VODiscountFix !== "undefined" && VODiscountFix.updateLineItemTotals) {
        VODiscountFix.updateLineItemTotals({ items: [item] });
        setTimeout(() => {
          VODiscountFix.updateLineItemTotals({ items: [item] });
        }, 120);
        setTimeout(() => {
          VODiscountFix.updateLineItemTotals({ items: [item] });
        }, 450);
      }
    });
    this._updating = false;
  },

  update(codesOverride) {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._doUpdate(codesOverride), 300);
  },

  _doUpdate(codesOverride) {
    const app = window.discountOnCartProApp;
    if (!app || typeof app.getDiscountedCart !== 'function') return;

    try {
      const cart = app.getDiscountedCart();
      const codes = codesOverride || app.codes || [];
      const normalizedCodes = this.maybeBeginCodeRemovalTransition(codes);
      // console.log('%c[插件数据] codes=' + JSON.stringify(codes), 'color:#f80;font-weight:bold');
      if (cart && cart.items) {
        cart.items.forEach(item => {
          // console.log('[插件]', item.title || item.key, '| 折扣=$' + (item.total_discount / 100).toFixed(2) + ' | 原价=$' + (item.original_line_price / 100).toFixed(2));
        });
      }

      if (this.pendingRemovalCodes && this.isCartStaleDuringCodeRemoval(cart, normalizedCodes)) {
        if (typeof VODiscountFix !== "undefined" && VODiscountFix.syncLineTotalsLoadingState) {
          VODiscountFix.syncLineTotalsLoadingState();
        }
        return;
      }

      this.render(cart, codes);
      if (this.pendingRemovalCodes) {
        this.endCodeRemovalTransition({ refresh: false });
      }
    } catch (e) {
      console.warn('[DrStackedDiscountTags] update failed:', e);
    }
  },

  // 折扣码变更后调用：用新码重新渲染 + 轮询等待第三方应用同步
  refreshAfterCodeChange(newCodes) {
    this.maybeBeginCodeRemovalTransition(newCodes);
    if (!this.pendingRemovalCodes) {
      // 立即用新码和当前的第三方应用折扣数据尝试渲染
      this.update(newCodes);
    } else if (typeof VODiscountFix !== "undefined" && VODiscountFix.syncLineTotalsLoadingState) {
      VODiscountFix.syncLineTotalsLoadingState();
    }

    // 轮询等待第三方应用自身数据更新完成
    [500, 1500, 3000, 5000].forEach(ms =>
      setTimeout(() => this.update(newCodes), ms)
    );
  },

  init() {
    document.addEventListener('docapp-discount-applied', () => {
      setTimeout(() => this.update(), 100);
    });

    if (typeof webvista !== 'undefined' && webvista.subscribe) {
      webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        const app = window.discountOnCartProApp;
        this.maybeBeginCodeRemovalTransition(app?.codes || []);
        if (this.pendingRemovalCodes && typeof VODiscountFix !== "undefined" && VODiscountFix.syncLineTotalsLoadingState) {
          VODiscountFix.syncLineTotalsLoadingState();
        }
        setTimeout(() => this.update(), 300);
      });
    }

    this._updating = false;
    const observer = new MutationObserver((mutations) => {
      if (this._updating) return;
      const dominated = mutations.every(m =>
        m.type === 'childList' &&
        m.target.classList &&
        m.target.classList.contains('dr-stacked-discounts')
      );
      if (dominated) return;
      const app = window.discountOnCartProApp;
      this.maybeBeginCodeRemovalTransition(app?.codes || []);
      if (this.pendingRemovalCodes && typeof VODiscountFix !== "undefined" && VODiscountFix.syncLineTotalsLoadingState) {
        VODiscountFix.syncLineTotalsLoadingState();
      }
      const placeholders = document.querySelectorAll('.dr-stacked-discounts');
      if (placeholders.length) this.update();
    });

    ['Cart-Drawer', 'Main-Cart'].forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el, { childList: true, subtree: true });
    });

    setTimeout(() => this.update(), 500);
  }
};

const DrQuickDiscountApply = {
  SUMMARY_SELECTORS: [
    ".docapp-cart-with-coupon-summary",
    ".docapp-widget[class*='docapp-cart-with-coupon-summary']",
  ],
  ENTRY_SELECTOR: "[data-dr-quick-discount-entry]",
  DEFAULT_OFFER_TEXT: "Special Offer",
  DEFAULT_CODE_LABEL: "Code:",
  mountTimer: null,
  observer: null,
  initialized: false,

  normalizeCode(value) {
    return String(value || "").trim();
  },

  normalizeDisplayText(value, fallback) {
    const normalized = String(value || "").trim();
    return normalized || fallback;
  },

  getSourceContainer(summaryRoot) {
    if (summaryRoot instanceof Element) {
      const closestContainer = summaryRoot.closest("#Main-Cart, #Cart-Drawer");
      if (closestContainer) return closestContainer;

      const scopedMainCart = summaryRoot.querySelector("#Main-Cart");
      if (scopedMainCart) return scopedMainCart;

      const scopedDrawer = summaryRoot.querySelector("#Cart-Drawer");
      if (scopedDrawer) return scopedDrawer;
    }

    return (
      document.getElementById("Main-Cart") ||
      document.getElementById("Cart-Drawer") ||
      null
    );
  },

  getConfigForSummary(summaryRoot) {
    const sourceContainer = this.getSourceContainer(summaryRoot);
    const sourceDataset = sourceContainer ? sourceContainer.dataset : {};

    const code = this.normalizeCode(sourceDataset.drQuickCode);
    const offerText = this.normalizeDisplayText(
      sourceDataset.drQuickOfferText,
      this.DEFAULT_OFFER_TEXT,
    );
    const codeLabel = this.normalizeDisplayText(
      sourceDataset.drQuickCodeLabel,
      this.DEFAULT_CODE_LABEL,
    );

    return {
      code,
      offerText,
      codeLabel,
    };
  },

  getSummaryRoots() {
    const roots = [];
    this.SUMMARY_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        if (!roots.includes(node)) roots.push(node);
      });
    });
    return roots;
  },

  createEntry(config) {
    const { code, offerText, codeLabel: codeLabelText } = config;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "dr-quick-discount-entry";
    button.setAttribute("data-dr-quick-discount-entry", "true");
    button.dataset.code = code;
    button.dataset.offerText = offerText;
    button.dataset.codeLabel = codeLabelText;

    const label = document.createElement("span");
    label.className = "dr-quick-discount-entry__label";
    label.textContent = offerText;

    const codeWrap = document.createElement("span");
    codeWrap.className = "dr-quick-discount-entry__code";
    const codePrefix = document.createElement("span");
    codePrefix.className = "dr-quick-discount-entry__code-prefix";
    codePrefix.textContent = codeLabelText;
    const codeValue = document.createElement("span");
    codeValue.className = "dr-quick-discount-entry__code-value";
    codeValue.textContent = code;
    codeWrap.append(codePrefix, document.createTextNode(" "), codeValue);

    button.append(label, codeWrap);
    button.addEventListener("click", (event) => {
      event.preventDefault();
      this.handleEntryClick(button);
    });

    return button;
  },

  ensureCodeLabelStructure(entry) {
    const label = entry.querySelector(".dr-quick-discount-entry__label");
    const codeLabel = entry.querySelector(".dr-quick-discount-entry__code");
    if (!codeLabel) return null;

    let codePrefix = codeLabel.querySelector(".dr-quick-discount-entry__code-prefix");
    let codeValue = codeLabel.querySelector(".dr-quick-discount-entry__code-value");

    if (!codePrefix || !codeValue) {
      codeLabel.textContent = "";
      codePrefix = document.createElement("span");
      codePrefix.className = "dr-quick-discount-entry__code-prefix";
      codePrefix.textContent = "Code:";
      codeValue = document.createElement("span");
      codeValue.className = "dr-quick-discount-entry__code-value";
      codeLabel.append(codePrefix, document.createTextNode(" "), codeValue);
    }

    return { label, codeLabel, codePrefix, codeValue };
  },

  updateEntry(entry, config) {
    const { code, offerText, codeLabel } = config;
    if (entry.dataset.code !== code) {
      entry.dataset.code = code;
    }
    if (entry.dataset.offerText !== offerText) {
      entry.dataset.offerText = offerText;
    }
    if (entry.dataset.codeLabel !== codeLabel) {
      entry.dataset.codeLabel = codeLabel;
    }

    const structure = this.ensureCodeLabelStructure(entry);
    if (!structure) return;

    const { label, codePrefix, codeValue } = structure;
    if (label && label.textContent !== offerText) {
      label.textContent = offerText;
    }
    if (codePrefix && codePrefix.textContent !== codeLabel) {
      codePrefix.textContent = codeLabel;
    }
    if (codeValue.textContent !== code) {
      codeValue.textContent = code;
    }
  },

  ensureEntry(summaryRoot, config) {
    let entry = summaryRoot.querySelector(this.ENTRY_SELECTOR);
    if (!entry) {
      entry = this.createEntry(config);
      summaryRoot.insertBefore(entry, summaryRoot.firstChild);
      return;
    }

    this.updateEntry(entry, config);
  },

  removeEntry(summaryRoot) {
    const entry = summaryRoot.querySelector(this.ENTRY_SELECTOR);
    if (entry) {
      entry.remove();
    }
  },

  getApplySearchRoots(summaryRoot) {
    const roots = [];
    const appendRoot = (node) => {
      if (!(node instanceof Element)) return;
      if (!roots.includes(node)) roots.push(node);
    };

    appendRoot(summaryRoot);
    appendRoot(summaryRoot.closest(".docapp-widget"));

    let current = summaryRoot.parentElement;
    let depth = 0;
    while (current && depth < 4) {
      const className = current.getAttribute("class") || "";
      if (className.includes("docapp")) {
        appendRoot(current);
      }
      current = current.parentElement;
      depth += 1;
    }

    return roots;
  },

  findApplyControls(summaryRoot) {
    const inputSelector =
      'input[name*="discount" i], input[name*="coupon" i], input[id*="discount" i], input[id*="coupon" i], input[placeholder*="discount" i], input[placeholder*="coupon" i]';
    const clickTargetSelector =
      "button, [role='button'], .docapp-coupon-input--button, [tabindex]";

    const roots = this.getApplySearchRoots(summaryRoot);
    for (const root of roots) {
      const input = root.querySelector(inputSelector);
      if (!input) continue;

      const candidates = [];
      const form = input.closest("form");
      if (form) {
        form.querySelectorAll(clickTargetSelector).forEach((button) => {
          if (!candidates.includes(button)) candidates.push(button);
        });
      }

      root.querySelectorAll(clickTargetSelector).forEach((button) => {
        if (!candidates.includes(button)) candidates.push(button);
      });

      const applyButton =
        candidates.find((button) => {
          if (!(button instanceof Element)) return false;
          if (button.classList.contains("dr-quick-discount-entry")) return false;
          if (button.matches("input")) return false;
          const text = (button.textContent || "").trim().toLowerCase();
          const className = (button.getAttribute("class") || "").toLowerCase();
          return text.includes("apply") || className.includes("coupon-input--button");
        }) || null;

      if (applyButton) return { input, applyButton };
    }

    return null;
  },

  setInputValue(input, value) {
    const inputProto = window.HTMLInputElement ? window.HTMLInputElement.prototype : null;
    const setter = inputProto
      ? Object.getOwnPropertyDescriptor(inputProto, "value")?.set
      : null;

    if (setter) {
      setter.call(input, value);
      return;
    }

    input.value = value;
  },

  triggerApply(applyButton) {
    if (!(applyButton instanceof Element)) return;

    if (typeof applyButton.focus === "function") {
      applyButton.focus();
    }

    const triggerMouse = (type) => {
      applyButton.dispatchEvent(
        new MouseEvent(type, { bubbles: true, cancelable: true, view: window }),
      );
    };

    const triggerKeyboard = (type) => {
      applyButton.dispatchEvent(
        new KeyboardEvent(type, {
          bubbles: true,
          cancelable: true,
          key: "Enter",
          code: "Enter",
        }),
      );
    };

    triggerMouse("mousedown");
    triggerMouse("mouseup");
    triggerMouse("click");
    triggerKeyboard("keydown");
    triggerKeyboard("keyup");
  },

  handleEntryClick(entry) {
    const code = this.normalizeCode(entry.dataset.code);
    if (!code) return;

    const summaryRoot = entry.closest(this.SUMMARY_SELECTORS.join(","));
    if (!summaryRoot) return;

    const controls = this.findApplyControls(summaryRoot);
    if (!controls) return;

    const { input, applyButton } = controls;

    entry.classList.add("is-applying");
    entry.setAttribute("aria-busy", "true");
    entry.setAttribute("disabled", "disabled");

    input.focus();
    this.setInputValue(input, code);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
    input.dispatchEvent(
      new KeyboardEvent("keydown", { bubbles: true, key: "Enter", code: "Enter" }),
    );
    input.dispatchEvent(new KeyboardEvent("keyup", { bubbles: true, key: "Enter" }));
    this.triggerApply(applyButton);

    setTimeout(() => {
      if (!document.body.contains(entry)) return;
      entry.classList.remove("is-applying");
      entry.removeAttribute("aria-busy");
      entry.removeAttribute("disabled");
    }, 1200);
  },

  mount() {
    const summaryRoots = this.getSummaryRoots();
    if (!summaryRoots.length) return;

    summaryRoots.forEach((root) => {
      const config = this.getConfigForSummary(root);
      if (!config.code) {
        this.removeEntry(root);
        return;
      }

      this.ensureEntry(root, config);
    });
  },

  scheduleMount(delay = 100) {
    clearTimeout(this.mountTimer);
    this.mountTimer = setTimeout(() => this.mount(), delay);
  },

  initObserver() {
    if (this.observer || !document.body) return;
    const summarySelector = this.SUMMARY_SELECTORS.join(",");
    const isSummaryNode = (node) => {
      if (!(node instanceof Element)) return false;
      return (
        node.matches(summarySelector) ||
        Boolean(node.closest(summarySelector))
      );
    };

    this.observer = new MutationObserver((mutations) => {
      const shouldMount = mutations.some((mutation) => {
        if (mutation.type !== "childList") return false;
        if (isSummaryNode(mutation.target)) return true;

        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
          if (!(node instanceof Element)) return false;
          return isSummaryNode(node) || Boolean(node.querySelector(summarySelector));
        });
      });
      if (!shouldMount) return;
      this.scheduleMount(120);
    });

    this.observer.observe(document.body, { childList: true, subtree: true });
  },

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.mount();
    this.initObserver();

    document.addEventListener("docapp-discount-applied", () => {
      this.scheduleMount(120);
    });

    if (typeof webvista !== "undefined" && webvista.subscribe) {
      webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.scheduleMount(160);
      });
    }

    window.addEventListener("load", () => {
      this.scheduleMount(120);
    });
  },
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    CartCheckoutWaitGuard.init();
    DrStackedDiscountTags.init();
    DrQuickDiscountApply.init();
  });
} else {
  CartCheckoutWaitGuard.init();
  DrStackedDiscountTags.init();
  DrQuickDiscountApply.init();
}
